import React, { useState, useEffect } from 'react';
import { Card, Container, Row, Col, Spinner, Tooltip, OverlayTrigger } from 'react-bootstrap';
import { NavSidebar } from './components/NavSidebar';
import { useAuth } from '../context/AuthContext';
import { useFilterContext } from '../context/SharedFilterContext';
import { useMike } from '../context/MikeContext';
import moment from 'moment';
import User from '../models/User';
import ActionLog from '../models/ActionLog';
import { ActionLogType } from '../models/ActionLogType';
import Wav from '../models/Wav';
import {
BrowserRouter as Router,
  useLocation,
  useHistory
} from "react-router-dom";
import { MdArrowBack } from 'react-icons/md';

import Company from '../models/Company';
import Property from '../models/Property';
import Equipment from '../models/Equipment';
import MultipointEvaluation from '../models/MultipointEvaluation';
import ZohoDevice from '../models/ZohoDevice';
import ZohoAccount from '../models/ZohoAccount';
import _ from 'lodash';
import DataTable from 'react-data-table-component';
import { EvaluationMark } from './components/EvaluationMark';
import Utils from '../common/Utils';
import { TAG_THRESHOLD_PERCENT, ACTION_THRESHOLD, NOD_SE_VALUE } from '../constants/Constants';

const CompanyAdminDashboard = () => {
  const history = useHistory();
  const {
    user,
    isAdminOrHigher
  } = useAuth();

  // Get date range and view mode from shared context
  const {
    startDate: sharedStartDate,
    endDate: sharedEndDate,
    inspectionViewMode,
    favoritedPropertyIds,
    propertyListIds
  } = useFilterContext();

  // Get Mike context for pushing dashboard data
  const { updateDashboardData, resetDashboardData } = useMike();

  // Convert shared dates to Date objects for compatibility
  const startDate = moment(sharedStartDate).toDate();
  const endDate = moment(sharedEndDate).toDate();

  const [isLoading, setIsLoading] = useState(false);
  const [activeUsersByRole, setActiveUsersByRole] = useState([]);
  const [uniqueMsiCount, setUniqueMsiCount] = useState(0);
  const [reportCount, setReportCount] = useState(0);
  const [filteredPropertyActivityHistory, setFilteredPropertyActivityHistory] = useState([]);
  const [propertiesInspectedCount, setPropertiesInspectedCount] = useState(0);
  const [totalPropertiesCount, setTotalPropertiesCount] = useState(0);
  const [criticalRedPoints, setCriticalRedPoints] = useState(0);
  const [criticalEvaluationsCount, setCriticalEvaluationsCount] = useState(0);

  // State for storing separate data sources for critical calculations
  const [pointInfosData, setPointInfosData] = useState([]);
  const [valsData, setValsData] = useState([]);
  const [figsEvaluationsData, setFigsEvaluationsData] = useState([]);

  const [isPropertiesExpanded, setIsPropertiesExpanded] = useState(false);
  const [inspectedPropertiesDetails, setInspectedPropertiesDetails] = useState([]);
  const [isPropertiesLoading, setIsPropertiesLoading] = useState(false);

  const [expandedUserRoles, setExpandedUserRoles] = useState(new Set());
  const [activeUsersDetails, setActiveUsersDetails] = useState([]);
  const [activeUsersDetailedData, setActiveUsersDetailedData] = useState([]);
  const [isUsersDetailsLoading, setIsUsersDetailsLoading] = useState(false);
  const [isActiveUsersLoading, setIsActiveUsersLoading] = useState(false);
  const [isMsiExpanded, setIsMsiExpanded] = useState(false);
  const [msiDetails, setMsiDetails] = useState([]);
  const [totalMsiCount, setTotalMsiCount] = useState(0);
  const [zohoDevices, setZohoDevices] = useState([]);
  const [zohoAccount, setZohoAccount] = useState(null);

  const [isReportsExpanded, setIsReportsExpanded] = useState(false);
  const [reportDetails, setReportDetails] = useState([]);
  const [reportDetailedData, setReportDetailedData] = useState([]);
  const [isReportsDetailsLoading, setIsReportsDetailsLoading] = useState(false);
  const [reportContextDetails, setReportContextDetails] = useState([]);

  // Critical Points Show Details
  const [isCriticalPointsExpanded, setIsCriticalPointsExpanded] = useState(false);
  const [criticalPointsDetails, setCriticalPointsDetails] = useState([]);
  const [isCriticalPointsLoading, setIsCriticalPointsLoading] = useState(false);

  // Critical Evaluations Show Details
  const [isCriticalEvalsExpanded, setIsCriticalEvalsExpanded] = useState(false);
  const [criticalEvalsDetails, setCriticalEvalsDetails] = useState([]);
  const [isCriticalEvalsLoading, setIsCriticalEvalsLoading] = useState(false);

  // On-demand loading state flags - track if each card has fetched data
  const [isActiveUsersFetched, setIsActiveUsersFetched] = useState(false);
  const [isMsiFetched, setIsMsiFetched] = useState(false);
  const [isPropertiesFetched, setIsPropertiesFetched] = useState(false);
  const [isReportsFetched, setIsReportsFetched] = useState(false);

  // Per-card loading states (for cards that don't have them yet)
  const [isMsiLoading, setIsMsiLoading] = useState(false);
  const [isReportsLoading, setIsReportsLoading] = useState(false);
  const [isPropertiesCardLoading, setIsPropertiesCardLoading] = useState(false);

  // All properties data for denominator table
  const [allPropertiesData, setAllPropertiesData] = useState([]);

  // Properties with Tasking Sheets card state
  const [propertiesWithChecksCount, setPropertiesWithChecksCount] = useState(0);
  const [propertiesWithChecksData, setPropertiesWithChecksData] = useState([]); // Raw filtered properties with checks
  const [propertiesWithChecksDetails, setPropertiesWithChecksDetails] = useState([]);
  const [isPropertiesWithChecksExpanded, setIsPropertiesWithChecksExpanded] = useState(false);
  const [isPropertiesWithChecksLoading, setIsPropertiesWithChecksLoading] = useState(false);

  const formatRoleTitle = (title) => {
    if (!title) return 'N/A';
    return title.replace(/^subdomain/i, '').replace(/^\w/, c => c.toUpperCase());
  };

  const fetchActiveUsers = async () => {
    if (!user || !user.company_id) {
      console.log("User or company ID is not available.");
      return;
    }

    setIsActiveUsersLoading(true);
    // Fetch users for the current company that are not archived
    const filters = [
      {"field": "company_id", "value": `${user.company_id}`, "comparison": "="},
      {"field": "archived", "value": "false", "comparison": "="}
    ];

    try {
      const results = await User.filterBy(filters, "last_login", "desc");
      const users = results.data;

      // Filter out users with excluded emails and users without roles
      const eligibleUsers = users.filter(user => {
        const isNotCompanyEmail = !user.email.endsWith("discoverdst.com");
        const isNotJohnJdst = user.email !== "johnjdst@me.com";
        const hasRole = user.roles && user.roles.length > 0 && user.roles[0];
        return isNotCompanyEmail && isNotJohnJdst && hasRole;
      });

      // Group users by role
      const roleMap = new Map();

      eligibleUsers.forEach(user => {
        const role = user.roles[0];
        const roleTitle = role.title;
        const clearanceLevel = role.clearance_level;

        if (!roleMap.has(roleTitle)) {
          roleMap.set(roleTitle, {
            roleTitle: roleTitle,
            clearanceLevel: clearanceLevel,
            totalCount: 0,
            activeCount: 0,
            activeUsers: []
          });
        }

        const roleData = roleMap.get(roleTitle);
        roleData.totalCount++;

        // Check if user is active in date range
        const lastLoginDate = moment(user.last_login).startOf('day');
        const isActive = lastLoginDate.isBetween(moment(startDate).startOf('day'), moment(endDate).endOf('day'), null, '[]');

        if (isActive) {
          roleData.activeCount++;
          roleData.activeUsers.push(user); // Store full user object instead of just name
        }
      });

      // Convert map to array, filter out roles with 0 active users, and sort by clearance level (ascending)
      const rolesArray = Array.from(roleMap.values())
        .filter(role => role.activeCount > 0)
        .sort((a, b) => a.clearanceLevel - b.clearanceLevel);


      setActiveUsersByRole(rolesArray);

      // Keep the details for expandable section (store full user objects)
      const allActiveUsers = rolesArray.flatMap(role => role.activeUsers);
      setActiveUsersDetails(allActiveUsers);

      // Fetch enriched details for Mike context (full enrichment)
      console.log('[Mike][Dashboard] Fetching enriched user activity details for Mike context...');
      const enrichedDetails = await fetchUserActivityDetails(allActiveUsers);
      setActiveUsersDetailedData(enrichedDetails); // Cache for DataTable as well

      // Push data to Mike context with enriched details
      updateDashboardData('activeUsers', {
        byRole: rolesArray,
        details: allActiveUsers,
        enrichedDetails: enrichedDetails
      });

    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setIsActiveUsersLoading(false);
      setIsActiveUsersFetched(true);
    }
  };

  const fetchUserActivityDetails = async (users) => {
    if (!users || users.length === 0) return [];
    try {
      const detailedUsers = await Promise.all(users.map(async (user) => {
        const userDetails = {
          fullName: user.full_name,
          role: user.roles && user.roles[0] ? formatRoleTitle(user.roles[0].title) : 'N/A',
          lastLogin: user.last_login,
          appVersion: 'N/A',
          msiConnection: null,
          msiSerial: 'N/A',
          deviceType: 'N/A',
          reportLink: null,
          recentProperty: 'N/A'
        };

        try {
          // Fetch Recent App Version (action_type_id 7 or 9)
          const appVersionLogs = await ActionLog.filterBy([
            { field: "user_uuid", value: user.uuid, comparison: "=" },
            { field: "action_type_id", value: JSON.stringify([ActionLogType.appUserLoggedIn, ActionLogType.appAutoLogin]), comparison: "IN" }
          ], "created_at", "desc", 1);

          if (appVersionLogs.data && appVersionLogs.data.length > 0) {
            userDetails.appVersion = appVersionLogs.data[0].app_version || 'N/A';
          }

          // Fetch Most Recent MSI Connection (action_type_id 1 or 8)
          const msiLogs = await ActionLog.filterBy([
            { field: "user_uuid", value: user.uuid, comparison: "=" },
            { field: "action_type_id", value: JSON.stringify([ActionLogType.msiConnected, ActionLogType.msiDisconnected]), comparison: "IN" }
          ], "created_at", "desc", 1);

          if (msiLogs.data && msiLogs.data.length > 0) {
            const msiLog = msiLogs.data[0];
            userDetails.msiConnection = msiLog.created_at;
            userDetails.msiSerial = msiLog.msi_serial || 'N/A';

            // Determine device type based on device_uuid length
            if (msiLog.device_uuid) {
              userDetails.deviceType = msiLog.device_uuid.length >= 17 ? 'Apple' : 'Android';
            }
          }

          // Fetch Recent Report (action_type_id 3)
          const reportLogs = await ActionLog.filterBy([
            { field: "user_uuid", value: user.uuid, comparison: "=" },
            { field: "action_type_id", value: ActionLogType.sentReport, comparison: "=" }
          ], "created_at", "desc", 1);

          if (reportLogs.data && reportLogs.data.length > 0) {
            try {
              const context = JSON.parse(reportLogs.data[0].context);
              if (context.reportS3Link) {
                // Replace domain as done in reports card
                userDetails.reportLink = context.reportS3Link.replace('dst-reports.s3.us-west-2.amazonaws.com', 'mv.dstapp.com/dst-reports');
              }
            } catch (parseError) {
              console.error("Error parsing report context:", parseError);
            }
          }

          // Fetch Recent Property Inspected (using Wav model)
          const wavResults = await Wav.filterBy([
            { field: "user_id", value: user.id, comparison: "=" }
          ], "recorded_at", "desc", 1);


          if (wavResults.data && wavResults.data.length > 0) {
            const propertyId = wavResults.data[0].property_id;
            if (propertyId) {
              // Fetch property name
              const propertyResults = await Property.filterBy([
                { field: "id", value: propertyId, comparison: "=" }
              ], "id", "asc", 1);

              if (propertyResults.data && propertyResults.data.length > 0) {
                userDetails.recentProperty = propertyResults.data[0].name;
              }
            }
          }
        } catch (error) {
          console.error(`Error fetching activity details for user ${user.uuid}:`, error);
        }

        return userDetails;
      }));

      return detailedUsers;
    } catch (error) {
      console.error("Error in fetchUserActivityDetails:", error);
      return [];
    }
  };

  const fetchReportDetailsEnriched = async (reports) => {
    if (!reports || reports.length === 0) return [];

    try {
      const enrichedReports = await Promise.all(reports.map(async (report) => {
        const reportDetails = {
          link: report.link,
          reportType: report.reportType,
          createdAt: report.createdAt,
          propertyUuid: report.propertyUuid,
          userName: 'N/A',
          propertyName: 'N/A'
        };

        try {
          // Fetch User name
          if (report.userUuid) {
            const userResults = await User.filterBy([
              { field: "uuid", value: report.userUuid, comparison: "=" }
            ], "id", "asc", 1);

            if (userResults.data && userResults.data.length > 0) {
              reportDetails.userName = userResults.data[0].full_name || 'N/A';
            }
          }

          // Fetch Property name
          if (report.propertyUuid) {
            const propertyResults = await Property.filterBy([
              { field: "uuid", value: report.propertyUuid, comparison: "=" }
            ], "id", "asc", 1);

            if (propertyResults.data && propertyResults.data.length > 0) {
              reportDetails.propertyName = propertyResults.data[0].name || 'N/A';
            }
          }
        } catch (error) {
          console.error(`Error fetching details for report:`, error);
        }

        return reportDetails;
      }));

      return enrichedReports;
    } catch (error) {
      console.error("Error in fetchReportDetailsEnriched:", error);
      return [];
    }
  };

  const fetchUniqueMsiCount = async () => {
    if (!user || !user.company_id) {
      return;
    }

    setIsMsiLoading(true);

    // Fetch all users for the current company
    const userFilters = [
      {"field": "company_id", "value": `${user.company_id}`, "comparison": "="},
      {"field": "archived", "value": "false", "comparison": "="}
    ];

    try {
      const userResults = await User.filterBy(userFilters, "last_login", "desc");
      const companyUsers = userResults.data;

      // Exclude users with emails ending in "discoverdst.com" and "johnjdst@me.com"
      const filteredCompanyUsers = companyUsers.filter(user => {
        const isNotCompanyEmail = !user.email.endsWith("discoverdst.com");
        const isNotJohnJdst = user.email !== "johnjdst@me.com";
        return isNotCompanyEmail && isNotJohnJdst;
      });

      const companyUserUUIDs = filteredCompanyUsers.map(user => user.uuid);
      const userMap = new Map(filteredCompanyUsers.map(user => [user.uuid, user.full_name]));

      // Fetch action logs for action_type_id 1 (MSI connected) within the date range
      // Filter by user_uuid at database level to reduce result set size
      const logFilters = [
        {"field": "action_type_id", "value": 1, "comparison": "="},
        {"field": "created_at", "value": startDate.toISOString(), "comparison": ">="},
        {"field": "created_at", "value": endDate.toISOString(), "comparison": "<="},
        {"field": "user_uuid", "value": JSON.stringify(companyUserUUIDs), "comparison": "IN"}
      ];

      const logResults = await ActionLog.filterBy(logFilters, "created_at", "desc", 10000);
      const logs = logResults.data;
      console.log("Action Logs for MSI connected:", logs);

      // Filter logs by checking if the user_uuid is in the list of filtered company user UUIDs
      const companyLogs = logs.filter(log => {
        const logDate = moment(log.created_at).startOf('day');
        return companyUserUUIDs.includes(log.user_uuid) && logDate.isBetween(moment(startDate).startOf('day'), moment(endDate).endOf('day'), null, '[]');
      });

      // Extract unique MSI serial numbers and associated user names (for DataTable backward compat)
      const uniqueMsiDetails = new Map();
      companyLogs.forEach(log => {
        if (!uniqueMsiDetails.has(log.msi_serial)) {
          uniqueMsiDetails.set(log.msi_serial, userMap.get(log.user_uuid));
        }
      });

      // Build enriched MSI details for Mike context: group all connections by serial
      const msiMap = new Map();
      companyLogs.forEach(log => {
        const serial = log.msi_serial;
        if (!msiMap.has(serial)) {
          msiMap.set(serial, { serial, users: [], connections: [], deviceTypes: new Set() });
        }
        const entry = msiMap.get(serial);
        const userName = userMap.get(log.user_uuid) || 'Unknown';
        if (!entry.users.includes(userName)) {
          entry.users.push(userName);
        }
        entry.connections.push({
          timestamp: log.created_at,
          userName,
          appVersion: log.app_version || null,
          msiVersion: log.msi_version || null
        });
        if (log.device_uuid) {
          entry.deviceTypes.add(log.device_uuid.length >= 17 ? 'Apple' : 'Android');
        }
      });

      // Fetch Zoho devices so we can filter used devices to only those the company actually owns
      const devices = await fetchTotalZohoDevices();
      const zohoSerials = new Set((devices || []).map(d => String(d.serial_number)));

      // Filter used MSI details to only include serials that exist in the company's Zoho device list
      const msiDetailsArray = Array.from(uniqueMsiDetails.entries())
        .filter(([serial]) => zohoSerials.has(String(serial)));
      setMsiDetails(msiDetailsArray);
      setUniqueMsiCount(msiDetailsArray.length);

      // Filter enriched details to match
      const enrichedMsiDetails = Array.from(msiMap.values())
        .filter(entry => zohoSerials.has(String(entry.serial)))
        .map(entry => {
          const sorted = entry.connections.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
          return {
            serial: entry.serial,
            users: entry.users,
            connectionCount: entry.connections.length,
            lastConnection: sorted[0]?.timestamp || null,
            deviceTypes: Array.from(entry.deviceTypes),
            latestAppVersion: sorted[0]?.appVersion || null,
            latestMsiVersion: sorted[0]?.msiVersion || null
          };
        });

      const usedSerials = new Set(msiDetailsArray.map(([serial]) => String(serial)));
      const zohoForMike = (devices || []).map(d => ({
        serial: d.serial_number,
        owner: d.device_owner,
        status: d.status,
        type: d.type,
        isUsed: usedSerials.has(String(d.serial_number))
      }));

      // Push enriched data to Mike context
      updateDashboardData('msiUsed', {
        count: msiDetailsArray.length,
        totalDevices: devices?.length || 0,
        details: msiDetailsArray,
        enrichedDetails: enrichedMsiDetails,
        zohoDevices: zohoForMike
      });

    } catch (error) {
      console.error("Error fetching action logs or users:", error);
    } finally {
      setIsMsiLoading(false);
      setIsMsiFetched(true);
    }
  };

  const fetchTotalZohoDevices = async () => {
    if (!user || !user.company_id) {
      return [];
    }
    try {
      const devices = await ZohoDevice.getByCompanyId(user.company_id);
      console.log("Zoho Devices for company:", user.company_id, devices);
      setZohoDevices(devices || []);
      setTotalMsiCount(devices?.length || 0);
      return devices || [];
    } catch (error) {
      console.error("Error fetching Zoho devices:", error);
      setZohoDevices([]);
      setTotalMsiCount(0);
      return [];
    }
  };

  const fetchReportCount = async () => {
    if (!user || !user.company_id) {
      return;
    }

    setIsReportsLoading(true);

    const filters = [
      {"field": "company_uuid", "value": `${user.company.uuid}`, "comparison": "="},
      {"field": "action_type_id", "value": 3, "comparison": "="}, // Action type for sent report
      {"field": "created_at", "value": startDate.toISOString(), "comparison": ">="},
      {"field": "created_at", "value": endDate.toISOString(), "comparison": "<="}
    ];

    // Add property filter based on view mode
    if (inspectionViewMode === 'favorited' && favoritedPropertyIds?.length > 0) {
      filters.push({
        "field": "property_uuid",
        "value": JSON.stringify(favoritedPropertyIds),
        "comparison": "IN"
      });
    } else if (inspectionViewMode === 'propertyList' && propertyListIds?.length > 0) {
      filters.push({
        "field": "property_uuid",
        "value": JSON.stringify(propertyListIds),
        "comparison": "IN"
      });
    }

    try {
      const results = await ActionLog.filterBy(filters, "created_at", "desc", 1000);
      const logs = results.data;

      const uniqueReportsMap = new Map();

      logs.forEach(log => {
        try {
          const context = JSON.parse(log.context);
          const reportTypeName = context.itemReportInput.reportTypeName;
          const propertyUUID = log.property_uuid;
          const userUUID = log.user_uuid;
          const reportDate = new Date(log.created_at).toISOString().split('T')[0];
          const reportMoment = moment(reportDate).startOf('day');

          if (reportMoment.isBetween(moment(startDate).startOf('day'), moment(endDate).endOf('day'), null, '[]')) {
            const reportIdentifier = `${reportDate}-${propertyUUID}-${reportTypeName}`;

            if (!uniqueReportsMap.has(reportIdentifier)) {
              // Replace the domain in the report link and add the /dst-reports path
              const originalLink = context.reportS3Link || 'No link available';
              const updatedLink = originalLink.replace('dst-reports.s3.us-west-2.amazonaws.com', 'mv.dstapp.com/dst-reports');

              uniqueReportsMap.set(reportIdentifier, {
                link: updatedLink, // Use the updated link
                reportType: reportTypeName || 'N/A',
                createdAt: log.created_at || 'N/A',
                userUuid: userUUID,
                propertyUuid: propertyUUID
              });
            }
          }
        } catch (error) {
          console.error("Error parsing context or extracting report details:", error);
        }
      });

      const reportDetailsArray = Array.from(uniqueReportsMap.values());
      setReportDetails(reportDetailsArray);
      setReportCount(uniqueReportsMap.size);

      // Fetch enriched details for Mike context (resolve user and property names)
      console.log('[Mike][Dashboard] Fetching enriched report details for Mike context...');
      const enrichedDetails = await fetchReportDetailsEnriched(reportDetailsArray);
      setReportDetailedData(enrichedDetails); // Cache for DataTable as well

      // Push data to Mike context with enriched details
      updateDashboardData('reports', {
        count: uniqueReportsMap.size,
        details: reportDetailsArray,
        enrichedDetails: enrichedDetails
      });
    } catch (error) {
      console.error("Error fetching report logs:", error);
    } finally {
      setIsReportsLoading(false);
      setIsReportsFetched(true);
    }
  };

  const fetchTotalPropertiesCount = async () => {
    if (!user || !user.company || !user.company.uuid) {
      return;
    }

    try {
      // Always fetch all company properties first (needed for denominator table)
      const propertiesInCompany = await Company.filteredProperty(
        user.id,
        user.company.uuid,
        null,  // No name filter - we want all properties
        null,  // No additional filter
        null   // No other parameters
      );

      console.log("Properties in company, denominator:", propertiesInCompany);

      let allProperties = [];
      if (propertiesInCompany && propertiesInCompany.data && propertiesInCompany.data.length > 0) {
        allProperties = propertiesInCompany.data[0].propertiesFilter || [];
      }

      // Apply view mode filtering
      let finalProperties;
      if (inspectionViewMode === 'favorited' && favoritedPropertyIds?.length > 0) {
        finalProperties = allProperties.filter(p => favoritedPropertyIds.includes(p.uuid));
      } else if (inspectionViewMode === 'propertyList' && propertyListIds?.length > 0) {
        finalProperties = allProperties.filter(p => propertyListIds.includes(p.uuid));
      } else {
        finalProperties = allProperties;
      }

      setAllPropertiesData(finalProperties);
      setTotalPropertiesCount(finalProperties.length);

      // Push all company properties to Mike context (denominator data)
      updateDashboardData('allCompanyProperties', {
        total: finalProperties.length,
        properties: finalProperties.map(p => ({
          name: p.name,
          lastReportRun: p.lastReportRun || null,
          lastEvaluation: p.last_evaluation_at || null,
          lastUpload: p.last_upload_at || null
        }))
      });
    } catch (error) {
      console.error("Error fetching total properties count:", error);
      setAllPropertiesData([]);
      setTotalPropertiesCount(0);
    }
  };

  const fetchFilteredPropertyActivityHistory = async () => {
    if (!user || !user.company_id) {
      console.log("User or company ID is not available.");
      return;
    }

    setIsPropertiesCardLoading(true);

    try {

      // Step 1: Fetch Property UUIDs
      const propertiesUuidInCompany = await Company.detailedFilteredPropertyUuidActivity(
        user.id,
        user.company.uuid,
        null,
        null, // No propertyNameFilter
        startDate,
        endDate
      );

      let sortedProperties = propertiesUuidInCompany?.data?.flatMap(company => company?.propertiesFilter)
        .sort((a, b) => moment(b.last_upload_at).diff(moment(a.last_upload_at)));

      // Filter properties based on view mode
      if (inspectionViewMode === 'favorited' && favoritedPropertyIds?.length > 0) {
        sortedProperties = sortedProperties?.filter(property =>
          favoritedPropertyIds.includes(property?.uuid)
        );
      } else if (inspectionViewMode === 'propertyList' && propertyListIds?.length > 0) {
        sortedProperties = sortedProperties?.filter(property =>
          propertyListIds.includes(property?.uuid)
        );
      }

      const propertyUUIDs = sortedProperties?.map(property => property?.uuid);

      // Step 2: Fetch Property Data in Batches in Parallel
      const batchSize = 10;
      const batchPromises = [];
      for (let i = 0; i < propertyUUIDs.length; i += batchSize) {
        const batchUUIDs = propertyUUIDs.slice(i, i + batchSize);
        batchPromises.push(Property.getCardData(batchUUIDs, user.id, startDate, endDate));
      }

      // Execute all batch requests in parallel
      const allPropertyDataBatches = await Promise.all(batchPromises);
      const allPropertyData = allPropertyDataBatches.flat();
      console.log("All property data:", allPropertyData);

      // Step 2b: Fetch critical inspections data separately for deterministic results
      // Fetch pointInfos, vals, and figs/evaluations in parallel
      console.log('Fetching critical inspections data separately...');
      const [fetchedPointInfosData, fetchedValsData, fetchedFigsEvaluationsData] = await Promise.all([
        Property.getPointInfosForProperties(propertyUUIDs),
        Property.getValsForProperties(propertyUUIDs, startDate, endDate),
        Property.getFigsWithEvaluations(propertyUUIDs, startDate, endDate)
      ]);

      console.log('Critical data fetched:', {
        pointInfosCount: fetchedPointInfosData?.length || 0,
        valsCount: fetchedValsData?.length || 0,
        figsEvaluationsCount: fetchedFigsEvaluationsData?.length || 0
      });

      // Store in state for use in detail views
      setPointInfosData(fetchedPointInfosData);
      setValsData(fetchedValsData);
      setFigsEvaluationsData(fetchedFigsEvaluationsData);

      // Add deterministic sorting to ensure consistent ordering across refreshes
      allPropertyData.sort((a, b) => {
        // Primary sort by last_upload_at (descending)
        const uploadCompare = moment(b.last_upload_at || 0).diff(moment(a.last_upload_at || 0));
        if (uploadCompare !== 0) return uploadCompare;
        // Secondary sort by UUID for deterministic ordering when dates are equal
        return a.uuid.localeCompare(b.uuid);
      });

      // Step 3: Filter and Sort Data
      const startMoment = moment(startDate).startOf('day');
      const endMoment = moment(endDate).endOf('day');

      // Filter for MSi card - only properties with vals in date range
      const filteredData = allPropertyData.filter(row => {
        const recordedAtDate = row?.vals?.edges[0]?.node?.recorded_at || '';
        const recordedAtMoment = moment(recordedAtDate).startOf('day');
        if (!recordedAtMoment.isValid()) return false;
        return recordedAtMoment.isBetween(startMoment, endMoment, null, '[]');
      }).sort((a, b) => {
        const dateA = moment(a?.vals?.edges[0]?.node?.recorded_at || '').startOf('day');
        const dateB = moment(b?.vals?.edges[0]?.node?.recorded_at || '').startOf('day');

        const dateDiff = dateB.diff(dateA);
        // Add secondary sort by UUID for deterministic ordering when dates are equal
        if (dateDiff !== 0) return dateDiff;
        return a.uuid.localeCompare(b.uuid);
      });

      // Step 4: Set State and Log
      setFilteredPropertyActivityHistory(filteredData);
      console.log("Filtered Property Activity History set in state:", filteredData);

      // Update the properties inspected count
      setPropertiesInspectedCount(filteredData.length);

      // Calculate properties with tasking sheet checks - filter from ALL properties (not just vals)
      const propertiesWithChecks = allPropertyData.filter(property => {
        const hasChecks = property.checks?.edges?.length > 0;
        if (!hasChecks) return false;

        // Verify at least one check is within the date range
        const checkEdges = property.checks?.edges || [];
        return checkEdges.some(edge => {
          const checkDate = edge.node?.created_at;
          if (!checkDate) return false;
          const checkMoment = moment(checkDate).startOf('day');
          return checkMoment.isBetween(startMoment, endMoment, null, '[]');
        });
      });
      setPropertiesWithChecksCount(propertiesWithChecks.length);
      setPropertiesWithChecksData(propertiesWithChecks);

      // Calculate critical red points using separate data
      let totalRedPoints = 0;

      // Process the separately fetched data for critical points
      fetchedPointInfosData.forEach(propertyPointData => {
        // Find matching vals data for this property
        const propertyValsData = fetchedValsData.find(p => p.uuid === propertyPointData.uuid);
        if (!propertyValsData) return;

        const allPointInfos = propertyPointData.pointInfos?.edges?.map(e => e.node) || [];
        const allVals = propertyValsData.vals?.edges?.map(e => e.node) || [];

        // Match ALL vals with pointInfos (every instance, not just most recent)
        const allMatchedPointInfos = allPointInfos.flatMap(pointInfo => {
          const matchingVals = allVals.filter(val => val.point_num === pointInfo.num);
          return matchingVals.map(matchingVal => {
            return {
              ...matchingVal,
              ...pointInfo,
              // Explicitly preserve val-specific reading data over pointInfo
              sig: matchingVal.sig,
              aiSig: matchingVal.aiSig,
              v: matchingVal.v,
              recorded_at: matchingVal.recorded_at,
              matchedValUuid: matchingVal.uuid,
            };
          });
        });

        // Count critical points (sig > 20 per individual reading)
        const redCount = allMatchedPointInfos.filter(pi => pi.sig > 20).length;
        totalRedPoints += redCount;
      });

      setCriticalRedPoints(totalRedPoints);
      console.log('Critical Points (using separate data):', totalRedPoints);

      // Calculate critical evaluations using separate figs data
      let totalCriticalEvaluations = 0;

      fetchedFigsEvaluationsData.forEach(propertyFigsData => {
        const figs = propertyFigsData.figs || [];

        figs.forEach(fig => {
          const criticality = fig.criticality || 1;
          const evaluations = fig.evaluations || [];

          // Note: evaluations are already filtered by date in getFigsWithEvaluations
          // Count critical evaluations (level * criticality > 20)
          const criticalCount = evaluations.filter(evaluation => {
            const evaluationScore = (evaluation.level || 0) * criticality;
            return evaluationScore > 20;
          }).length;

          totalCriticalEvaluations += criticalCount;
        });
      });

      setCriticalEvaluationsCount(totalCriticalEvaluations);
      console.log('Critical Evaluations (using separate data):', totalCriticalEvaluations);

      // Compute enriched details for Mike context (full enrichment)
      console.log('[Mike][Dashboard] Computing enriched property details for Mike context...');

      // 1. Compute enriched properties details (same as togglePropertiesDetails)
      const propertiesEnrichedDetails = await Promise.all(filteredData.map(async (property) => {
        const allVals = (property.vals?.edges || []).map(edge => edge.node);
        const allPointInfos = (property.pointInfos?.edges || []).map(edge => edge.node);

        const allMatchedPointInfos = allPointInfos.map(pointInfo => {
          const matchingVal = allVals.find(val => val.point_num === pointInfo.num);
          if (matchingVal) {
            return {
              ...pointInfo,
              matchedValUuid: matchingVal.uuid,
              evalNum: pointInfo.evalNum
            };
          }
          return null;
        }).filter(info => info && info.matchedValUuid);

        const uniquePointNumCount = new Set(allVals.map(val => val.point_num)).size;
        const totalCount = property.pointInfos?.totalCount || 0;
        const percentageInspected = totalCount > 0 ? Math.floor((uniquePointNumCount / totalCount) * 100) : 0;
        const healthScore = calculatePerpetualScore(allMatchedPointInfos);

        // Extract last uploader with fallback
        const lastUploader = await resolveUserName(property.lastValUploaded);

        // Determine the most recent inspection date
        const recordedAt = property.vals?.edges[0]?.node?.recorded_at;
        const createdAt = property.checks?.edges[0]?.node?.created_at;
        const datesWithValues = [{ date: recordedAt }, { date: createdAt }].filter(d => d.date);
        const lastInspection = datesWithValues.length
          ? moment(datesWithValues.reduce((a, b) => moment(a.date).isAfter(moment(b.date)) ? a : b).date).local().format("M-D-YY")
          : 'N/A';

        // Calculate critical evaluations for this property
        const propertyFigsData = fetchedFigsEvaluationsData.find(p => p.uuid === property.uuid);
        let criticalEvalsCount = 0;
        if (propertyFigsData) {
          const figs = propertyFigsData.figs || [];
          figs.forEach(fig => {
            const criticality = fig.criticality || 1;
            const evaluations = fig.evaluations || [];
            criticalEvalsCount += evaluations.filter(evaluation => {
              const evaluationScore = (evaluation.level || 0) * criticality;
              return evaluationScore > 20;
            }).length;
          });
        }

        return {
          propertyUuid: property.uuid,
          propertyName: property.name,
          criticalPoint: allMatchedPointInfos.filter(pi => pi.evalNum > 20).length,
          criticalEvaluations: criticalEvalsCount,
          inspected: uniquePointNumCount,
          totalPoints: totalCount,
          percentInspected: percentageInspected,
          healthScore: healthScore,
          lastUploader: lastUploader,
          lastInspection: lastInspection
        };
      }));

      setInspectedPropertiesDetails(propertiesEnrichedDetails); // Cache for DataTable

      // 2. Compute critical points details (same as toggleCriticalPointsDetails)
      const criticalPointsTableData = [];
      fetchedPointInfosData.forEach(propertyPointData => {
        const propertyValsData = fetchedValsData.find(p => p.uuid === propertyPointData.uuid);
        if (!propertyValsData) return;

        const allPointInfos = propertyPointData.pointInfos?.edges?.map(e => e.node) || [];
        const allVals = propertyValsData.vals?.edges?.map(e => e.node) || [];

        // Match ALL vals with pointInfos (every instance, not just most recent)
        const allMatchedPointInfos = allPointInfos.flatMap(pointInfo => {
          const matchingVals = allVals.filter(val => val.point_num === pointInfo.num);
          return matchingVals.map(matchingVal => {
            return {
              ...matchingVal,
              ...pointInfo,
              // Explicitly preserve val-specific reading data over pointInfo
              sig: matchingVal.sig,
              aiSig: matchingVal.aiSig,
              v: matchingVal.v,
              recorded_at: matchingVal.recorded_at,
              matchedValUuid: matchingVal.uuid,
            };
          });
        });

        const criticalPoints = allMatchedPointInfos.filter(pi => pi.sig > 20);
        criticalPoints.forEach(point => {
          criticalPointsTableData.push({
            propertyUuid: propertyPointData.uuid,
            property: point.property_name || propertyPointData.name,
            rootComp: extractRootCompForPoint(point),
            pointName: point.measurement_name,
            soundSignatures: extractSoundSignaturesForPoint(point),
            actions: extractActionsForPoint(point),
            inspectionDate: point.recorded_at,
            soundEnergy: point.v === NOD_SE_VALUE ? "VISUAL" : point.v,
            evaluation: point.sig,
            soundEvaluator: point.aiSig,
            wavUserId: point.wav?.user_id || null,
            technician: point.wav?.user?.full_name || null
          });
        });
      });

      // Batch-resolve technician names from user_ids
      const unresolvedCriticalUserIds = [...new Set(criticalPointsTableData.filter(row => !row.technician && row.wavUserId).map(row => row.wavUserId))];
      if (unresolvedCriticalUserIds.length > 0) {
        const userNameMap = new Map();
        await Promise.all(unresolvedCriticalUserIds.map(async (userId) => {
          try {
            const user = await User.findById(userId);
            if (user?.full_name) userNameMap.set(userId, user.full_name);
          } catch (e) { /* skip failed lookups */ }
        }));
        criticalPointsTableData.forEach(row => {
          if (!row.technician && row.wavUserId) {
            row.technician = userNameMap.get(row.wavUserId) || 'N/A';
          }
        });
      }
      criticalPointsTableData.forEach(row => { if (!row.technician) row.technician = 'N/A'; });

      setCriticalPointsDetails(criticalPointsTableData); // Cache for DataTable

      // 3. Compute critical evaluations details (same as toggleCriticalEvalsDetails)
      const criticalEvalsTableData = [];
      fetchedFigsEvaluationsData.forEach(propertyFigsData => {
        const figs = propertyFigsData.figs || [];
        figs.forEach(fig => {
          const criticality = fig.criticality || 1;
          const evaluations = fig.evaluations || [];
          const criticalEvals = evaluations.filter(evaluation => {
            const evaluationScore = (evaluation.level || 0) * criticality;
            return evaluationScore > 20;
          });

          criticalEvals.forEach(evaluation => {
            const mpeTags = parseMPETags(evaluation.recorded_yhat_json);
            const mpeActions = parseMPEActions(evaluation.recorded_yhat_json);

            criticalEvalsTableData.push({
              propertyUuid: propertyFigsData.uuid,
              property: propertyFigsData.name,
              equipment: fig.name,
              createdAt: evaluation.created_at,
              soundEvaluator: (evaluation.sound_level || 0) * criticality,
              evaluation: (evaluation.level || 0) * criticality,
              mpeTags: mpeTags,
              mpeActions: mpeActions
            });
          });
        });
      });

      // Resolve MPE UUIDs to names for Mike context
      await Promise.all(criticalEvalsTableData.map(async (row) => {
        if (row.mpeTags && row.mpeTags.length > 0) {
          row.mpeTagNames = await fetchNamesByUUIDs(row.mpeTags, MultipointEvaluation.findTagsByUUID);
        }
        if (row.mpeActions && row.mpeActions.length > 0) {
          row.mpeActionNames = await fetchNamesByUUIDs(row.mpeActions, MultipointEvaluation.findActionsByUUID);
        }
      }));

      setCriticalEvalsDetails(criticalEvalsTableData); // Cache for DataTable

      console.log('[Mike][Dashboard] Enriched data computed:', {
        propertiesCount: propertiesEnrichedDetails.length,
        criticalPointsCount: criticalPointsTableData.length,
        criticalEvalsCount: criticalEvalsTableData.length
      });

      // Push data to Mike context with enriched details
      updateDashboardData('propertiesInspected', {
        count: filteredData.length,
        total: totalPropertiesCount,
        details: filteredData,
        enrichedDetails: propertiesEnrichedDetails
      });

      updateDashboardData('criticalInspections', {
        redPoints: totalRedPoints,
        evaluations: totalCriticalEvaluations,
        pointInfos: fetchedPointInfosData,
        vals: fetchedValsData,
        figsEvaluations: fetchedFigsEvaluationsData,
        criticalPointsDetails: criticalPointsTableData,
        criticalEvalsDetails: criticalEvalsTableData
      });

      // Push tasking sheets data to Mike context
      const startMomentTS = moment(startDate).startOf('day');
      const endMomentTS = moment(endDate).endOf('day');
      const taskingSheetsEnriched = propertiesWithChecks.map(property => {
        const checksInRange = (property.checks?.edges || []).filter(edge => {
          const checkMoment = moment(edge.node?.created_at).startOf('day');
          return checkMoment.isBetween(startMomentTS, endMomentTS, null, '[]');
        });
        const lastCheck = checksInRange[0]?.node;
        return {
          propertyName: property.name,
          lastCheckDate: lastCheck?.created_at
            ? moment(lastCheck.created_at).local().format('M-D-YY HH:mm')
            : 'N/A',
          lastCheckUser: lastCheck?.user?.full_name || 'N/A',
          checksInPeriod: checksInRange.length
        };
      });

      updateDashboardData('taskingSheets', {
        count: propertiesWithChecks.length,
        total: totalPropertiesCount,
        details: taskingSheetsEnriched
      });

    } catch (error) {
      console.error("Error fetching property activity history:", error);
    } finally {
      setIsPropertiesCardLoading(false);
      setIsPropertiesFetched(true);
    }
  };

  const calculatePerpetualScore = (points) => {
    const multipliers = { E1: 4, E2: 3, E3: 2, E4: 1, E5: 0 };
    let numerator = 0;
    let denominator = points.length * 4;
    points.forEach(point => {
      if (point.evalNum !== null) {
        let category;
        if (point.evalNum >= 1 && point.evalNum <= 5) category = 'E1';
        else if (point.evalNum >= 6 && point.evalNum <= 10) category = 'E2';
        else if (point.evalNum >= 11 && point.evalNum <= 15) category = 'E3';
        else if (point.evalNum >= 16 && point.evalNum <= 20) category = 'E4';
        else if (point.evalNum >= 21 && point.evalNum <= 25) category = 'E5';
        if (category) {
          numerator += multipliers[category];
        }
      }
    });
    return Math.round((numerator / denominator) * 100);
  };

  // Helper function to resolve user name with fallback to User.findById
  const resolveUserName = async (lastValUploaded) => {
    // First try: embedded wav.user.full_name
    if (lastValUploaded?.wav?.user?.full_name) {
      return lastValUploaded.wav.user.full_name;
    }

    // Second try: embedded user.full_name (top-level)
    if (lastValUploaded?.user?.full_name) {
      return lastValUploaded.user.full_name;
    }

    // Third try: fetch by wav.user_id
    if (lastValUploaded?.wav?.user_id) {
      try {
        const user = await User.findById(lastValUploaded.wav.user_id);
        if (user && user.full_name) {
          return user.full_name;
        }
      } catch (error) {
        console.error('Error fetching user by id:', error);
      }
    }

    return 'N/A';
  };

  // Helper functions for Critical Points and Evaluations Show Details
  const extractRootCompForPoint = (pointInfo) => {
    const hierarchy = {
      adamFigName: pointInfo?.adam_fig_name,
      component1: pointInfo?.parent_fig_name ? pointInfo?.fig_name : "",
      component2: pointInfo?.grandparent_fig_name ? pointInfo?.parent_fig_name : "",
      component3: pointInfo?.adam_fig_name ? (pointInfo?.grandparent_fig_name ? pointInfo.grandparent_fig_name : "") : "",
    };

    return hierarchy.component1 ? `${hierarchy.adamFigName} / ${hierarchy.component1}` : hierarchy.adamFigName;
  };

  const extractSoundSignaturesForPoint = (val) => {
    const filteredSignatures = [
      val?.accelse ? "Accelerating SE" : null,
      ...(val?.signatures ? (val.signatures.filter(sig => (sig.pr * 100) >= TAG_THRESHOLD_PERCENT)).map(x => x.signature.name) : [])
    ].filter(x => x != null);

    return filteredSignatures.join(' ');
  };

  const extractActionsForPoint = (val) => {
    const filteredActionNames = (val?.actions || [])
      .filter(action => action.pr > ACTION_THRESHOLD)
      .map(x => x.action && x.action.name)
      .filter(x => x != null);

    return filteredActionNames.join(', ');
  };

  const parseMPETags = (recorded_yhat_json) => {
    if (!recorded_yhat_json) return [];

    try {
      const parsedYhatJson = JSON.parse(recorded_yhat_json);
      let parsedNode;
      if (parsedYhatJson.nodes) {
        parsedNode = parsedYhatJson.nodes[0];
      } else {
        parsedNode = parsedYhatJson;
      }
      return parsedNode.tagUuids || [];
    } catch (error) {
      console.error("Error parsing recorded_yhat_json for tags:", error);
      return [];
    }
  };

  const parseMPEActions = (recorded_yhat_json) => {
    if (!recorded_yhat_json) return [];

    try {
      const parsedYhatJson = JSON.parse(recorded_yhat_json);
      let parsedNode;
      if (parsedYhatJson.nodes) {
        parsedNode = parsedYhatJson.nodes[0];
      } else {
        parsedNode = parsedYhatJson;
      }
      return parsedNode.actionUuids || [];
    } catch (error) {
      console.error("Error parsing recorded_yhat_json for actions:", error);
      return [];
    }
  };

  const fetchNamesByUUIDs = async (uuids, fetchFunction) => {
    if (!uuids || uuids.length === 0) return [];

    const names = await Promise.all(uuids.map(async (uuid) => {
      try {
        const result = await fetchFunction(uuid);
        return result.length > 0 ? result[0].name : uuid;
      } catch (error) {
        console.error(`Error fetching name for UUID ${uuid}:`, error);
        return uuid;
      }
    }));
    return names;
  };

  const togglePropertiesDetails = async () => {
    if (!isPropertiesExpanded && inspectedPropertiesDetails.length === 0) {
      // Only fetch if not already cached
      setIsPropertiesLoading(true);
      setIsPropertiesExpanded(true); // Set expansion immediately so spinner shows
      try {
        // Prepare data for DataTable - use Promise.all for async user name resolution
        const tableData = await Promise.all(filteredPropertyActivityHistory.map(async (property) => {
          // Filter and match points
          const allVals = (property.vals.edges || []).map(edge => edge.node);
          const allPointInfos = (property.pointInfos.edges || []).map(edge => edge.node);

          const allMatchedPointInfos = allPointInfos.map(pointInfo => {
            const matchingVal = allVals.find(val => val.point_num === pointInfo.num);
            if (matchingVal) {
              return {
                ...pointInfo,
                matchedValUuid: matchingVal.uuid,
                evalNum: pointInfo.evalNum // Correctly use evalNum from pointInfo
              };
            }
            return null;
          }).filter(info => info && info.matchedValUuid);

          const uniquePointNumCount = new Set(allVals.map(val => val.point_num)).size;
          const totalCount = property.pointInfos.totalCount || 0;
          const percentageInspected = totalCount > 0 ? Math.floor((uniquePointNumCount / totalCount) * 100) : 0;
          const healthScore = calculatePerpetualScore(allMatchedPointInfos);

          // Extract last uploader with fallback to User.findById
          const lastUploader = await resolveUserName(property.lastValUploaded);

          // Determine the most recent inspection date
          const recordedAt = property.vals?.edges[0]?.node?.recorded_at;
          const createdAt = property.checks?.edges[0]?.node?.created_at;
          const datesWithValues = [
            { date: recordedAt },
            { date: createdAt }
          ].filter(d => d.date);

          const lastInspection = datesWithValues.length
            ? moment(datesWithValues.reduce((a, b) => moment(a.date).isAfter(moment(b.date)) ? a : b).date).local().format("M-D-YY")
            : 'N/A';

          // Calculate critical evaluations for this property
          const propertyFigsData = figsEvaluationsData.find(p => p.uuid === property.uuid);
          let criticalEvalsCount = 0;
          if (propertyFigsData) {
            const figs = propertyFigsData.figs || [];
            figs.forEach(fig => {
              const criticality = fig.criticality || 1;
              const evaluations = fig.evaluations || [];
              criticalEvalsCount += evaluations.filter(evaluation => {
                const evaluationScore = (evaluation.level || 0) * criticality;
                return evaluationScore > 20;
              }).length;
            });
          }

          return {
            propertyUuid: property.uuid,
            propertyName: property.name,
            criticalPoint: allMatchedPointInfos.filter(pi => pi.evalNum > 20).length, // Calculate red count
            criticalEvaluations: criticalEvalsCount,
            inspected: uniquePointNumCount,
            totalPoints: totalCount,
            percentInspected: percentageInspected,
            healthScore: healthScore,
            lastUploader: lastUploader,
            lastInspection: lastInspection
          };
        }));

        setInspectedPropertiesDetails(tableData);
      } catch (error) {
        console.error("Error preparing property details for DataTable:", error);
      } finally {
        setIsPropertiesLoading(false);
      }
    } else {
      // Toggle expansion state when collapsing or data is already cached
      setIsPropertiesExpanded(!isPropertiesExpanded);
    }
  };

  const togglePropertiesWithChecksDetails = async () => {
    if (!isPropertiesWithChecksExpanded && propertiesWithChecksDetails.length === 0) {
      setIsPropertiesWithChecksLoading(true);
      setIsPropertiesWithChecksExpanded(true);

      try {
        const startMoment = moment(startDate).startOf('day');
        const endMoment = moment(endDate).endOf('day');

        // Use pre-filtered propertiesWithChecksData from fetch
        const tableData = propertiesWithChecksData.map((property) => {
          // Get checks within date range
          const checksInRange = (property.checks?.edges || []).filter(edge => {
            const checkMoment = moment(edge.node.created_at).startOf('day');
            return checkMoment.isBetween(startMoment, endMoment, null, '[]');
          });

          // Get last check info
          const lastCheck = checksInRange[0]?.node;
          const lastCheckDate = lastCheck?.created_at
            ? moment(lastCheck.created_at).local().format('M-D-YY HH:mm')
            : 'N/A';
          const lastCheckUser = lastCheck?.user?.full_name || 'N/A';

          return {
            propertyUuid: property.uuid,
            propertyName: property.name,
            lastCheckDate: lastCheckDate,
            lastCheckUser: lastCheckUser
          };
        });

        setPropertiesWithChecksDetails(tableData);
      } catch (error) {
        console.error("Error preparing properties with checks details:", error);
      } finally {
        setIsPropertiesWithChecksLoading(false);
      }
    } else {
      setIsPropertiesWithChecksExpanded(!isPropertiesWithChecksExpanded);
    }
  };

  const toggleRoleDetails = async (roleTitle) => {
    // If data not yet fetched, fetch it first
    if (activeUsersDetailedData.length === 0 && activeUsersDetails.length > 0) {
      setIsUsersDetailsLoading(true);
      try {
        const detailedData = await fetchUserActivityDetails(activeUsersDetails);
        setActiveUsersDetailedData(detailedData);
      } catch (error) {
        console.error("Error fetching user activity details:", error);
      } finally {
        setIsUsersDetailsLoading(false);
      }
    }
    setExpandedUserRoles(prev => {
      const next = new Set(prev);
      if (next.has(roleTitle)) next.delete(roleTitle);
      else next.add(roleTitle);
      return next;
    });
  };

  const toggleMsiDetails = () => {
    setIsMsiExpanded(!isMsiExpanded);
  };

  const toggleReportsDetails = async () => {
    if (!isReportsExpanded && reportDetailedData.length === 0) {
      // Only fetch detailed data if not already fetched
      setIsReportsDetailsLoading(true);
      setIsReportsExpanded(true); // Set expansion immediately so spinner shows
      try {
        const detailedData = await fetchReportDetailsEnriched(reportDetails);
        setReportDetailedData(detailedData);
      } catch (error) {
        console.error("Error fetching report details:", error);
      } finally {
        setIsReportsDetailsLoading(false);
      }
    } else {
      // Toggle expansion state when collapsing or data is already cached
      setIsReportsExpanded(!isReportsExpanded);
    }
  };

  const toggleCriticalPointsDetails = async () => {
    if (!isCriticalPointsExpanded && criticalPointsDetails.length === 0) {
      // Only fetch if not already cached
      setIsCriticalPointsLoading(true);
      setIsCriticalPointsExpanded(true); // Set expansion immediately so spinner shows
      try {
        const tableData = [];

        // Use the same data sources as the count calculation
        pointInfosData.forEach(propertyPointData => {
          const propertyValsData = valsData.find(p => p.uuid === propertyPointData.uuid);
          if (!propertyValsData) return;

          const allPointInfos = propertyPointData.pointInfos?.edges?.map(e => e.node) || [];
          const allVals = propertyValsData.vals?.edges?.map(e => e.node) || [];

          // Match ALL vals with pointInfos (every instance, not just most recent)
          const allMatchedPointInfos = allPointInfos.flatMap(pointInfo => {
            const matchingVals = allVals.filter(val => val.point_num === pointInfo.num);
            return matchingVals.map(matchingVal => {
              return {
                ...matchingVal,
                ...pointInfo,
                // Explicitly preserve val-specific reading data over pointInfo
                sig: matchingVal.sig,
                aiSig: matchingVal.aiSig,
                v: matchingVal.v,
                recorded_at: matchingVal.recorded_at,
                matchedValUuid: matchingVal.uuid,
              };
            });
          });

          // Filter for critical points (sig > 20 per individual reading)
          const criticalPoints = allMatchedPointInfos.filter(pi => pi.sig > 20);

          // Transform to table rows
          criticalPoints.forEach(point => {
            tableData.push({
              propertyUuid: propertyPointData.uuid,
              property: point.property_name || propertyPointData.name,
              rootComp: extractRootCompForPoint(point),
              pointName: point.measurement_name,
              soundSignatures: extractSoundSignaturesForPoint(point),
              actions: extractActionsForPoint(point),
              inspectionDate: point.recorded_at,
              soundEnergy: point.v === NOD_SE_VALUE ? "VISUAL" : point.v,
              evaluation: point.sig,
              soundEvaluator: point.aiSig,
              wavUserId: point.wav?.user_id || null,
              technician: point.wav?.user?.full_name || null
            });
          });
        });

        // Batch-resolve technician names from user_ids
        const unresolvedUserIds = [...new Set(tableData.filter(row => !row.technician && row.wavUserId).map(row => row.wavUserId))];
        if (unresolvedUserIds.length > 0) {
          const userNameMap = new Map();
          await Promise.all(unresolvedUserIds.map(async (userId) => {
            try {
              const user = await User.findById(userId);
              if (user?.full_name) userNameMap.set(userId, user.full_name);
            } catch (e) { /* skip failed lookups */ }
          }));
          tableData.forEach(row => {
            if (!row.technician && row.wavUserId) {
              row.technician = userNameMap.get(row.wavUserId) || 'N/A';
            }
          });
        }
        tableData.forEach(row => { if (!row.technician) row.technician = 'N/A'; });

        setCriticalPointsDetails(tableData);
      } catch (error) {
        console.error("Error preparing critical points details:", error);
      } finally {
        setIsCriticalPointsLoading(false);
      }
    } else {
      // Toggle expansion state when collapsing or data is already cached
      setIsCriticalPointsExpanded(!isCriticalPointsExpanded);
    }
  };

  const toggleCriticalEvalsDetails = async () => {
    if (!isCriticalEvalsExpanded && criticalEvalsDetails.length === 0) {
      // Only fetch if not already cached
      setIsCriticalEvalsLoading(true);
      setIsCriticalEvalsExpanded(true); // Set expansion immediately so spinner shows
      try {
        const tableData = [];

        // Use the same data source as the count calculation
        figsEvaluationsData.forEach(propertyFigsData => {
          const figs = propertyFigsData.figs || [];

          figs.forEach(fig => {
            const criticality = fig.criticality || 1;
            const evaluations = fig.evaluations || [];

            // Note: evaluations are already filtered by date in getFigsWithEvaluations
            // Filter for critical evaluations (level * criticality > 20)
            const criticalEvals = evaluations.filter(evaluation => {
              const evaluationScore = (evaluation.level || 0) * criticality;
              return evaluationScore > 20;
            });

            // Transform to table rows with UUID->name resolution
            criticalEvals.forEach(evaluation => {
              const mpeTags = parseMPETags(evaluation.recorded_yhat_json);
              const mpeActions = parseMPEActions(evaluation.recorded_yhat_json);

              tableData.push({
                propertyUuid: propertyFigsData.uuid,
                property: propertyFigsData.name,
                equipment: fig.name,
                createdAt: evaluation.created_at,
                soundEvaluator: (evaluation.sound_level || 0) * criticality,
                evaluation: (evaluation.level || 0) * criticality,
                mpeTags: mpeTags,
                mpeActions: mpeActions,
                evaluationObj: evaluation
              });
            });
          });
        });

        // Resolve MPE UUIDs to names
        await Promise.all(tableData.map(async (row) => {
          if (row.mpeTags && row.mpeTags.length > 0) {
            row.mpeTagNames = await fetchNamesByUUIDs(row.mpeTags, MultipointEvaluation.findTagsByUUID);
          }
          if (row.mpeActions && row.mpeActions.length > 0) {
            row.mpeActionNames = await fetchNamesByUUIDs(row.mpeActions, MultipointEvaluation.findActionsByUUID);
          }
        }));

        setCriticalEvalsDetails(tableData);
      } catch (error) {
        console.error("Error preparing critical evaluations details:", error);
      } finally {
        setIsCriticalEvalsLoading(false);
      }
    } else {
      // Toggle expansion state when collapsing or data is already cached
      setIsCriticalEvalsExpanded(!isCriticalEvalsExpanded);
    }
  };

  // Helper function to escape CSV values
  const escapeCsvValue = (value) => {
    if (value === null || value === undefined) return '';
    const stringValue = String(value);
    // If value contains comma, quotes, or newline, wrap in quotes and escape existing quotes
    if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  const downloadDataTableCsv = () => {
    if (!inspectedPropertiesDetails.length) {
      console.log("No data available to download.");
      return;
    }

    // Define the headers for the CSV, including the new columns
    const headers = [
      'Property',
      'Critical Point',
      'Critical Evaluations',
      '# of Inspected',
      '# of Total Points',
      '% Inspected',
      'Health Score',
      'Last Uploader',
      'Last Inspection'
    ];

    // Map the data to CSV format, including the new details
    const rows = inspectedPropertiesDetails.map(detail => [
      escapeCsvValue(detail.propertyName),
      escapeCsvValue(detail.criticalPoint),
      escapeCsvValue(detail.criticalEvaluations),
      escapeCsvValue(detail.inspected),
      escapeCsvValue(detail.totalPoints),
      escapeCsvValue(detail.percentInspected),
      escapeCsvValue(detail.healthScore),
      escapeCsvValue(detail.lastUploader),
      escapeCsvValue(detail.lastInspection)
    ]);

    // Combine the headers and rows, then join them with new lines to form the CSV content
    const csvContent = [
      `Company: ${user.company.title}`,
      `Report: Inspected Properties Details`,
      `Date Range: ${moment(startDate).format('YYYY-MM-DD')} to ${moment(endDate).format('YYYY-MM-DD')}`,
      '', // Blank line to separate headers from table
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    // Create a blob with the CSV content and trigger a download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const fileName = `inspected_properties_details_${user.company.title.replace(/\s+/g, '_')}_${moment().format('YYYYMMDD_HHmmss')}.csv`;
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Export Properties with Tasking Sheets to CSV
  const downloadPropertiesWithChecksCsv = () => {
    if (!propertiesWithChecksDetails.length) {
      console.log("No properties with checks data available to download.");
      return;
    }

    const headers = [
      'Property',
      'Last Check Date',
      'Last Check User'
    ];

    const rows = propertiesWithChecksDetails.map(detail => [
      escapeCsvValue(detail.propertyName),
      escapeCsvValue(detail.lastCheckDate),
      escapeCsvValue(detail.lastCheckUser)
    ]);

    const csvContent = [
      `Company: ${user.company.title}`,
      `Report: Properties Inspected with Tasking Sheets`,
      `Date Range: ${moment(startDate).format('YYYY-MM-DD')} to ${moment(endDate).format('YYYY-MM-DD')}`,
      '',
      `Summary: ${propertiesWithChecksCount} of ${totalPropertiesCount} properties have tasking sheet activity`,
      '',
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const fileName = `properties_with_tasking_sheets_${user.company.title.replace(/\s+/g, '_')}_${moment().format('YYYYMMDD_HHmmss')}.csv`;
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Export All Properties (Denominator) to CSV
  const downloadAllPropertiesCsv = () => {
    if (!allPropertiesData.length) {
      console.log("No properties data available to download.");
      return;
    }

    const headers = [
      'Property',
      'Last Report Run',
      'Last Evaluation',
      'Last Upload',
      'Inspected in Period'
    ];

    const inspectedPropertyNames = new Set(inspectedPropertiesDetails.map(ip => ip.propertyName));

    const rows = allPropertiesData.map(property => [
      escapeCsvValue(property.name),
      escapeCsvValue(property.lastReportRun ? moment(property.lastReportRun).format('YYYY-MM-DD') : ''),
      escapeCsvValue(property.last_evaluation_at ? moment(property.last_evaluation_at).format('YYYY-MM-DD') : ''),
      escapeCsvValue(property.last_upload_at ? moment(property.last_upload_at).format('YYYY-MM-DD') : ''),
      escapeCsvValue(inspectedPropertyNames.has(property.name) ? 'Yes' : 'No')
    ]);

    const csvContent = [
      `Company: ${user.company.title}`,
      `Report: All Company Properties`,
      `Date Range: ${moment(startDate).format('YYYY-MM-DD')} to ${moment(endDate).format('YYYY-MM-DD')}`,
      '',
      `Summary: ${propertiesInspectedCount} of ${totalPropertiesCount} properties inspected in period`,
      '',
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const fileName = `all_properties_${user.company.title.replace(/\s+/g, '_')}_${moment().format('YYYYMMDD_HHmmss')}.csv`;
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Export Active Users Details to CSV (filtered by role)
  const downloadActiveUsersCsv = (roleTitle) => {
    const formattedRole = formatRoleTitle(roleTitle);
    const filteredData = activeUsersDetailedData.filter(u => u.role === formattedRole);

    if (!filteredData.length) {
      console.log("No active users data available to download.");
      return;
    }

    const headers = [
      'Full Name',
      'Recent Activity',
      'App Version',
      'MSI Connection',
      'Tool Serial',
      'Device',
      'Recent Report',
      'Recent Property'
    ];

    const rows = filteredData.map(user => [
      escapeCsvValue(user.fullName || ''),
      escapeCsvValue(user.lastLogin || ''),
      escapeCsvValue(user.appVersion || ''),
      escapeCsvValue(user.msiConnection || ''),
      escapeCsvValue(user.msiSerial || ''),
      escapeCsvValue(user.deviceType || ''),
      escapeCsvValue(user.reportLink || ''),
      escapeCsvValue(user.recentProperty || '')
    ]);

    const csvContent = [
      `Company: ${user.company.title}`,
      `Report: ${formattedRole} Active Users Details`,
      `Date Range: ${moment(startDate).format('YYYY-MM-DD')} to ${moment(endDate).format('YYYY-MM-DD')}`,
      '',
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const fileName = `active_users_${formattedRole.replace(/\s+/g, '_')}_${user.company.title.replace(/\s+/g, '_')}_${moment().format('YYYYMMDD_HHmmss')}.csv`;
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Export Critical Points Details to CSV
  const downloadCriticalPointsCsv = () => {
    if (!criticalPointsDetails.length) {
      console.log("No critical points data available to download.");
      return;
    }

    const headers = [
      'Property',
      'Root/Comp',
      'Point Name',
      'SoundSignatures™',
      'Actions',
      'Inspection Date',
      'SoundEnergy™',
      'Evaluation',
      'SoundEvaluator™',
      'Technician'
    ];

    const rows = criticalPointsDetails.map(point => [
      escapeCsvValue(point.property),
      escapeCsvValue(point.rootComp),
      escapeCsvValue(point.pointName),
      escapeCsvValue(point.soundSignatures),
      escapeCsvValue(point.actions),
      escapeCsvValue(point.inspectionDate ? moment(point.inspectionDate).format('YYYY-MM-DD HH:mm:ss') : ''),
      escapeCsvValue(point.soundEnergy),
      escapeCsvValue(point.evaluation),
      escapeCsvValue(point.soundEvaluator),
      escapeCsvValue(point.technician)
    ]);

    const csvContent = [
      `Company: ${user.company.title}`,
      `Report: Critical Points Details`,
      `Date Range: ${moment(startDate).format('YYYY-MM-DD')} to ${moment(endDate).format('YYYY-MM-DD')}`,
      '',
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const fileName = `critical_points_${user.company.title.replace(/\s+/g, '_')}_${moment().format('YYYYMMDD_HHmmss')}.csv`;
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Export Critical Evaluations Details to CSV
  const downloadCriticalEvalsCsv = () => {
    if (!criticalEvalsDetails.length) {
      console.log("No critical evaluations data available to download.");
      return;
    }

    const headers = [
      'Property',
      'Equipment',
      'Created At',
      'SoundEvaluator™',
      'Evaluation',
      'MPE Tags',
      'MPE Actions'
    ];

    const rows = criticalEvalsDetails.map(evalItem => [
      escapeCsvValue(evalItem.property),
      escapeCsvValue(evalItem.equipment),
      escapeCsvValue(evalItem.createdAt ? moment(evalItem.createdAt).format('YYYY-MM-DD HH:mm:ss') : ''),
      escapeCsvValue(evalItem.soundEvaluator),
      escapeCsvValue(evalItem.evaluation),
      // Join MPE tags with semicolons, using names if available, otherwise UUIDs
      escapeCsvValue((evalItem.mpeTagNames || evalItem.mpeTags || []).join('; ')),
      // Join MPE actions with semicolons, using names if available, otherwise UUIDs
      escapeCsvValue((evalItem.mpeActionNames || evalItem.mpeActions || []).join('; '))
    ]);

    const csvContent = [
      `Company: ${user.company.title}`,
      `Report: Critical Evaluations Details`,
      `Date Range: ${moment(startDate).format('YYYY-MM-DD')} to ${moment(endDate).format('YYYY-MM-DD')}`,
      '',
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const fileName = `critical_evaluations_${user.company.title.replace(/\s+/g, '_')}_${moment().format('YYYYMMDD_HHmmss')}.csv`;
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Export MSI Details to CSV
  const downloadMsiDetailsCsv = () => {
    if (!msiDetails.length && !zohoDevices.length) {
      console.log("No MSI data available to download.");
      return;
    }

    const csvSections = [
      `Company: ${user.company.title}`,
      `Report: MSI Devices Details`,
      `Date Range: ${moment(startDate).format('YYYY-MM-DD')} to ${moment(endDate).format('YYYY-MM-DD')}`,
      '',
      `Summary: ${msiDetails.length} devices used out of ${totalMsiCount} total devices`,
      '',
      '--- MSI DEVICES USED IN SELECTED PERIOD ---',
      ''
    ];

    // Section 1: Used MSI Devices
    const usedHeaders = ['Serial', 'User'];
    csvSections.push(usedHeaders.join(','));

    if (msiDetails.length > 0) {
      msiDetails.forEach(([serial, userName]) => {
        csvSections.push([
          escapeCsvValue(serial),
          escapeCsvValue(userName || 'N/A')
        ].join(','));
      });
    } else {
      csvSections.push('No MSI devices used in this period.');
    }

    csvSections.push('');
    csvSections.push('--- ALL COMPANY MSI DEVICES ---');
    csvSections.push('');

    // Section 2: All Company MSI Devices
    const allHeaders = ['Serial', 'Owner', 'Status', 'Type', 'Used in Period'];
    csvSections.push(allHeaders.join(','));

    if (zohoDevices.length > 0) {
      zohoDevices.forEach(device => {
        const isUsed = msiDetails.some(([serial]) => String(serial) === String(device.serial_number));
        csvSections.push([
          escapeCsvValue(device.serial_number),
          escapeCsvValue(device.device_owner || '-'),
          escapeCsvValue(device.status || '-'),
          escapeCsvValue(device.type || '-'),
          escapeCsvValue(isUsed ? 'Yes' : 'No')
        ].join(','));
      });
    } else {
      csvSections.push('No MSI devices found for this company.');
    }

    const csvContent = csvSections.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const fileName = `msi_devices_${user.company.title.replace(/\s+/g, '_')}_${moment().format('YYYYMMDD_HHmmss')}.csv`;
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Export Reports Details to CSV
  const downloadReportsCsv = () => {
    if (!reportDetailedData.length) {
      console.log("No reports data available to download.");
      return;
    }

    const headers = [
      'Report Type',
      'Created At',
      'User',
      'Property',
      'Report URL'
    ];

    const rows = reportDetailedData.map(report => [
      escapeCsvValue(report.reportType),
      escapeCsvValue(report.createdAt ? moment(report.createdAt).format('YYYY-MM-DD HH:mm:ss') : ''),
      escapeCsvValue(report.userName),
      escapeCsvValue(report.propertyName),
      // Use the actual link field which contains the URL
      escapeCsvValue(report.link || 'N/A')
    ]);

    const csvContent = [
      `Company: ${user.company.title}`,
      `Report: Generated Reports Details`,
      `Date Range: ${moment(startDate).format('YYYY-MM-DD')} to ${moment(endDate).format('YYYY-MM-DD')}`,
      '',
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const fileName = `reports_${user.company.title.replace(/\s+/g, '_')}_${moment().format('YYYYMMDD_HHmmss')}.csv`;
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      await Promise.all([
        fetchFilteredPropertyActivityHistory(),
        fetchActiveUsers(),
        fetchUniqueMsiCount(),
        fetchReportCount(),
        fetchTotalPropertiesCount()
      ]);
    } catch (error) {
      console.error("fetchData: Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch ZohoAccount on mount - independent of filters
  useEffect(() => {
    const fetchZohoAccount = async () => {
      if (!user || !user.company_id) return;
      try {
        const accounts = await ZohoAccount.getByCompanyId(user.company_id);
        if (accounts && accounts.length > 0) {
          setZohoAccount(accounts[0]);
        }
      } catch (error) {
        console.error("Error fetching Zoho account:", error);
      }
    };
    fetchZohoAccount();
  }, [user?.company_id]);

  // Reset state when filters change (on-demand loading pattern)
  useEffect(() => {
    // Clear cached detailed data when filters change
    setActiveUsersDetailedData([]);
    setInspectedPropertiesDetails([]);
    setReportDetailedData([]);
    setCriticalPointsDetails([]);
    setCriticalEvalsDetails([]);
    // Clear the new data sources as well
    setPointInfosData([]);
    setValsData([]);
    setFigsEvaluationsData([]);

    // Reset fetched flags so cards show "Get Results" again
    setIsActiveUsersFetched(false);
    setIsMsiFetched(false);
    setIsPropertiesFetched(false);
    setIsReportsFetched(false);

    // Reset data to initial state
    setActiveUsersByRole([]);
    setUniqueMsiCount(0);
    setTotalMsiCount(0);
    setZohoDevices([]);
    setReportCount(0);
    setPropertiesInspectedCount(0);
    setTotalPropertiesCount(0);
    setAllPropertiesData([]);
    setPropertiesWithChecksCount(0);
    setPropertiesWithChecksData([]);
    setPropertiesWithChecksDetails([]);
    setIsPropertiesWithChecksExpanded(false);
    setCriticalRedPoints(0);
    setCriticalEvaluationsCount(0);
    setMsiDetails([]);
    setReportDetails([]);
    setFilteredPropertyActivityHistory([]);

    // Set isLoading to false since we're not auto-fetching
    setIsLoading(false);

    // Reset Mike's dashboard data as well
    resetDashboardData();

    // DO NOT call fetchData() - let user trigger on-demand
  }, [sharedStartDate, sharedEndDate, inspectionViewMode, favoritedPropertyIds, propertyListIds, user?.company_id, resetDashboardData]);


  const renderTooltip = (props) => (
    <Tooltip id="button-tooltip" {...props}>
      DST Login Activity
    </Tooltip>
  );

  const renderMsiTooltip = (props) => (
    <Tooltip id="msi-tooltip" {...props}>
      Most Recent Sync Date: {zohoAccount?.last_updated ? moment(zohoAccount.last_updated).format('MM/DD/YY HH:mm') : 'N/A'}
    </Tooltip>
  );

  return (
    <div className="d-flex flex-row justify-content-start align-items-start" style={{ width: '100%', height: '100vh' }}>
      <NavSidebar />
      <Container fluid style={{ padding: '20px', marginTop: '20px', backgroundColor: '#f8f9fa', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)', overflow: 'auto', marginLeft: '20px', marginRight: '20px' }}>

        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '20px', marginBottom: '20px', position: 'relative' }}>
          <button
            onClick={() => history.push('/migration')}
            style={{
              position: 'absolute',
              left: 0,
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center'
            }}
            aria-label="Go back"
          >
            <MdArrowBack size={24} />
          </button>
          <h2 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>Company Admin Dashboard</h2>
          <button
            onClick={fetchData}
            disabled={isLoading}
            style={{
              fontSize: '14px',
              fontWeight: 'bold',
              padding: '10px 20px',
              borderRadius: '8px',
              backgroundColor: '#007bff',
              color: '#ffffff',
              border: 'none',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.6 : 1
            }}
          >
            {isLoading ? 'Loading All...' : 'GET ALL RESULTS'}
          </button>
        </div>

        <Row>
            <Col md={expandedUserRoles.size > 0 ? 12 : 6} lg={expandedUserRoles.size > 0 ? 12 : 4}>
              <Card className="mb-3" style={{ boxShadow: '0 4px 8px rgba(0,0,0,0.2)', borderRadius: '12px', borderColor: '#e0e0e0', transition: 'transform 0.3s', background: '#ffffff', width: '100%' }}>
                <Card.Body>
                  <Card.Title style={{ fontSize: '18px', fontWeight: 'bold', position: 'relative' }}>
                    Active Users
                    <OverlayTrigger
                      placement="top"
                      delay={{ show: 250, hide: 400 }}
                      overlay={renderTooltip}
                    >
                      <span style={{ position: 'absolute', top: '0', right: '0', cursor: 'pointer', color: '#007bff', fontSize: '20px' }}>
                        ⓘ
                      </span>
                    </OverlayTrigger>
                  </Card.Title>
                  {!isActiveUsersFetched ? (
                    // Show "Get Results" button when not fetched
                    <div style={{ marginTop: '20px', textAlign: 'center' }}>
                      <button
                        onClick={fetchActiveUsers}
                        disabled={isActiveUsersLoading}
                        style={{
                          fontSize: '14px',
                          fontWeight: 'bold',
                          padding: '8px 16px',
                          borderRadius: '8px',
                          backgroundColor: '#007bff',
                          color: '#ffffff',
                          border: 'none',
                          cursor: isActiveUsersLoading ? 'not-allowed' : 'pointer',
                          opacity: isActiveUsersLoading ? 0.6 : 1
                        }}
                      >
                        {isActiveUsersLoading ? 'Loading...' : 'Get Results'}
                      </button>
                    </div>
                  ) : (
                    <>
                      {/* Display roles with active/total counts and per-role details */}
                      <div style={{ marginTop: '10px' }}>
                        {activeUsersByRole.map((role, index) => {
                          const isRoleExpanded = expandedUserRoles.has(role.roleTitle);
                          const roleData = activeUsersDetailedData.filter(u => u.role === formatRoleTitle(role.roleTitle));
                          return (
                            <div key={index} style={{ marginBottom: '20px', borderBottom: index < activeUsersByRole.length - 1 ? '1px solid #eee' : 'none', paddingBottom: '15px' }}>
                              <div style={{ fontSize: '14px', fontWeight: '600', color: '#666', marginBottom: '5px' }}>
                                {formatRoleTitle(role.roleTitle)}
                              </div>
                              <div style={{ fontSize: '20px', fontWeight: 'bold' }}>
                                {role.activeCount}/{role.totalCount}
                              </div>
                              <span
                                onClick={() => toggleRoleDetails(role.roleTitle)}
                                style={{ cursor: 'pointer', color: '#007bff', fontSize: '14px', display: 'inline-block', marginTop: '5px' }}
                              >
                                {isRoleExpanded ? 'Hide Details' : 'Show Details'}
                              </span>
                              {isRoleExpanded && (
                                isUsersDetailsLoading ? (
                                  <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: '150px', marginTop: '10px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                                    <Spinner animation="border" role="status" style={{ width: '2rem', height: '2rem' }}>
                                      <span className="sr-only">Loading...</span>
                                    </Spinner>
                                    <p style={{ marginTop: '10px', color: '#666', fontSize: '13px' }}>Loading activity details...</p>
                                  </div>
                                ) : (
                                  <div style={{ marginTop: '10px', width: '100%', overflowX: 'auto' }}>
                                    <DataTable
                                      title={`${formatRoleTitle(role.roleTitle)} Activity Details`}
                                      columns={[
                                        {
                                          name: 'Full Name',
                                          selector: row => row.fullName,
                                          sortable: true,
                                          minWidth: '150px',
                                          wrap: true
                                        },
                                        {
                                          name: 'Recent Activity',
                                          selector: row => row.lastLogin,
                                          format: row => row.lastLogin ? moment(row.lastLogin).format('MM/DD/YY') : 'N/A',
                                          sortable: true,
                                          minWidth: '120px'
                                        },
                                        {
                                          name: 'Recent App Version',
                                          selector: row => row.appVersion,
                                          sortable: true,
                                          minWidth: '100px'
                                        },
                                        {
                                          name: 'Recent MSI Connection',
                                          selector: row => row.msiConnection,
                                          format: row => row.msiConnection ? moment(row.msiConnection).format('MM/DD/YY HH:mm') : 'N/A',
                                          sortable: true,
                                          minWidth: '140px'
                                        },
                                        {
                                          name: 'Recent Tool Serial',
                                          selector: row => row.msiSerial,
                                          sortable: true,
                                          minWidth: '120px',
                                          sortFunction: (rowA, rowB) => {
                                            const a = rowA.msiSerial;
                                            const b = rowB.msiSerial;
                                            if (a === 'N/A' && b === 'N/A') return 0;
                                            if (a === 'N/A') return 1;
                                            if (b === 'N/A') return -1;
                                            return parseFloat(a) - parseFloat(b);
                                          }
                                        },
                                        {
                                          name: 'Recent Device',
                                          selector: row => row.deviceType,
                                          sortable: true,
                                          minWidth: '80px'
                                        },
                                        {
                                          name: 'Recent Report',
                                          selector: row => row.reportLink,
                                          sortable: true,
                                          format: row => row.reportLink ? (
                                            <a
                                              href={row.reportLink}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              style={{ color: '#007bff', fontSize: '12px' }}
                                            >
                                              View
                                            </a>
                                          ) : 'N/A',
                                          minWidth: '100px'
                                        },
                                        {
                                          name: 'Recent Property Inspected',
                                          selector: row => row.recentProperty,
                                          sortable: true,
                                          minWidth: '150px',
                                          wrap: true
                                        }
                                      ]}
                                      data={roleData}
                                      dense
                                      striped
                                      highlightOnHover
                                      pagination
                                      paginationPerPage={25}
                                      paginationRowsPerPageOptions={[25, 50, 100]}
                                      style={{ fontSize: '12px' }}
                                    />
                                    <button
                                      onClick={() => downloadActiveUsersCsv(role.roleTitle)}
                                      disabled={!roleData.length}
                                      style={{
                                        marginTop: '10px',
                                        fontSize: '14px',
                                        fontWeight: 'bold',
                                        padding: '8px 16px',
                                        borderRadius: '8px',
                                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                                        transition: 'transform 0.3s',
                                        backgroundColor: roleData.length ? '#28a745' : '#6c757d',
                                        color: '#ffffff',
                                        border: 'none',
                                        cursor: roleData.length ? 'pointer' : 'not-allowed',
                                        opacity: roleData.length ? 1 : 0.6
                                      }}
                                    >
                                      Download Table Data (CSV)
                                    </button>
                                  </div>
                                )
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={4}>
              <Card className="mb-3" style={{ boxShadow: '0 4px 8px rgba(0,0,0,0.2)', borderRadius: '12px', borderColor: '#e0e0e0', transition: 'transform 0.3s', background: '#f7f7f7' }}>
                <Card.Body>
                  <Card.Title style={{ fontSize: '18px', fontWeight: 'bold', position: 'relative' }}>
                    MSi Utilization
                    <OverlayTrigger
                      placement="top"
                      delay={{ show: 250, hide: 400 }}
                      overlay={renderMsiTooltip}
                    >
                      <span style={{ position: 'absolute', top: '0', right: '0', cursor: 'pointer', color: '#007bff', fontSize: '20px' }}>
                        ⓘ
                      </span>
                    </OverlayTrigger>
                  </Card.Title>
                  {!isMsiFetched ? (
                    // Show "Get Results" button when not fetched
                    <div style={{ marginTop: '20px', textAlign: 'center' }}>
                      <button
                        onClick={fetchUniqueMsiCount}
                        disabled={isMsiLoading}
                        style={{
                          fontSize: '14px',
                          fontWeight: 'bold',
                          padding: '8px 16px',
                          borderRadius: '8px',
                          backgroundColor: '#007bff',
                          color: '#ffffff',
                          border: 'none',
                          cursor: isMsiLoading ? 'not-allowed' : 'pointer',
                          opacity: isMsiLoading ? 0.6 : 1
                        }}
                      >
                        {isMsiLoading ? 'Loading...' : 'Get Results'}
                      </button>
                    </div>
                  ) : (
                    <>
                      <Card.Text style={{ fontSize: '20px' }}>{uniqueMsiCount} / {totalMsiCount}</Card.Text>
                      <span
                        onClick={toggleMsiDetails}
                        style={{ cursor: 'pointer', color: '#007bff', fontSize: '14px' }}
                      >
                        {isMsiExpanded ? 'Hide Details' : 'Show Details'}
                      </span>
                      {isMsiExpanded && (
                        <div style={{ marginTop: '15px' }}>
                          {/* Section 1: MSI Devices Used in Selected Period */}
                          <div style={{
                            backgroundColor: '#e8f5e9',
                            borderRadius: '8px',
                            padding: '12px',
                            marginBottom: '15px'
                          }}>
                            <h6 style={{
                              fontSize: '12px',
                              fontWeight: 'bold',
                              color: '#2e7d32',
                              marginBottom: '10px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>
                              MSI Devices Used in Selected Period ({msiDetails.length})
                            </h6>
                            {msiDetails.length > 0 ? (
                              <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                                  <thead>
                                    <tr style={{ borderBottom: '1px solid #c8e6c9' }}>
                                      <th style={{ textAlign: 'left', padding: '6px 8px', color: '#1b5e20' }}>Serial</th>
                                      <th style={{ textAlign: 'left', padding: '6px 8px', color: '#1b5e20' }}>User</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {msiDetails.map(([serial, userName], index) => (
                                      <tr key={index} style={{ borderBottom: '1px solid #e0e0e0' }}>
                                        <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{serial}</td>
                                        <td style={{ padding: '6px 8px' }}>{userName}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <p style={{ color: '#666', fontSize: '13px', margin: 0 }}>No MSI devices used in this period.</p>
                            )}
                          </div>

                          {/* Section 2: All Company MSI Devices */}
                          <div style={{
                            backgroundColor: '#f5f5f5',
                            borderRadius: '8px',
                            padding: '12px'
                          }}>
                            <h6 style={{
                              fontSize: '12px',
                              fontWeight: 'bold',
                              color: '#616161',
                              marginBottom: '10px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>
                              All Company MSI Devices ({totalMsiCount})
                            </h6>
                            {zohoDevices.length > 0 ? (
                              <div style={{ overflowX: 'auto', maxHeight: '250px', overflowY: 'auto' }}>
                                <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                                  <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f5f5f5' }}>
                                    <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                                      <th style={{ textAlign: 'left', padding: '6px 8px', color: '#424242' }}>Serial</th>
                                      <th style={{ textAlign: 'left', padding: '6px 8px', color: '#424242' }}>Owner</th>
                                      <th style={{ textAlign: 'left', padding: '6px 8px', color: '#424242' }}>Status</th>
                                      <th style={{ textAlign: 'left', padding: '6px 8px', color: '#424242' }}>Type</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {zohoDevices.map((device, index) => {
                                      const isUsed = msiDetails.some(([serial]) => String(serial) === String(device.serial_number));
                                      return (
                                        <tr
                                          key={device.id || index}
                                          style={{
                                            borderBottom: '1px solid #e0e0e0',
                                            backgroundColor: isUsed ? '#e8f5e9' : 'transparent'
                                          }}
                                        >
                                          <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>
                                            {device.serial_number}
                                            {isUsed && <span style={{ marginLeft: '6px', color: '#2e7d32' }}>●</span>}
                                          </td>
                                          <td style={{ padding: '6px 8px' }}>{device.device_owner || '-'}</td>
                                          <td style={{ padding: '6px 8px' }}>{device.status || '-'}</td>
                                          <td style={{ padding: '6px 8px' }}>{device.type || '-'}</td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <p style={{ color: '#666', fontSize: '13px', margin: 0 }}>No MSI devices found for this company.</p>
                            )}
                          </div>

                          {/* Download Button */}
                          <button
                            onClick={downloadMsiDetailsCsv}
                            disabled={!msiDetails.length && !zohoDevices.length}
                            style={{
                              marginTop: '15px',
                              fontSize: '14px',
                              fontWeight: 'bold',
                              padding: '8px 16px',
                              borderRadius: '8px',
                              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                              transition: 'transform 0.3s',
                              backgroundColor: (msiDetails.length || zohoDevices.length) ? '#28a745' : '#6c757d',
                              color: '#ffffff',
                              border: 'none',
                              cursor: (msiDetails.length || zohoDevices.length) ? 'pointer' : 'not-allowed',
                              opacity: (msiDetails.length || zohoDevices.length) ? 1 : 0.6
                            }}
                          >
                            Download Table Data (CSV)
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </Card.Body>
              </Card>
            </Col>
            <Col md={isPropertiesExpanded ? 12 : 6} lg={isPropertiesExpanded ? 12 : 4}>
              <Card
                className="mb-3"
                style={{
                  boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                  borderRadius: '12px',
                  borderColor: '#e0e0e0',
                  transition: 'transform 0.3s',
                  background: '#ffffff',
                  width: '100%',
                  overflowX: 'auto'
                }}
              >
                <Card.Body>
                  <Card.Title style={{ fontSize: '18px', fontWeight: 'bold' }}>
                    Properties Inspected w/MSi
                  </Card.Title>
                  {!isPropertiesFetched ? (
                    // Show "Get Results" button when not fetched
                    <div style={{ marginTop: '20px', textAlign: 'center' }}>
                      <button
                        onClick={() => {
                          fetchFilteredPropertyActivityHistory();
                          fetchTotalPropertiesCount();
                        }}
                        disabled={isPropertiesCardLoading}
                        style={{
                          fontSize: '14px',
                          fontWeight: 'bold',
                          padding: '8px 16px',
                          borderRadius: '8px',
                          backgroundColor: '#007bff',
                          color: '#ffffff',
                          border: 'none',
                          cursor: isPropertiesCardLoading ? 'not-allowed' : 'pointer',
                          opacity: isPropertiesCardLoading ? 0.6 : 1
                        }}
                      >
                        {isPropertiesCardLoading ? 'Loading...' : 'Get Results'}
                      </button>
                    </div>
                  ) : (
                    <>
                      <Card.Text style={{ fontSize: '20px' }}>
                        {propertiesInspectedCount} / {totalPropertiesCount}
                      </Card.Text>
                      <span
                        onClick={togglePropertiesDetails}
                        style={{ cursor: 'pointer', color: '#007bff', fontSize: '14px' }}
                      >
                        {isPropertiesExpanded ? 'Hide Details' : 'Show Details'}
                      </span>
                      {isPropertiesLoading ? (
                        <div className="d-flex justify-content-center align-items-center" style={{ height: '50px' }}>
                          <Spinner animation="border" role="status">
                            <span className="sr-only">Loading...</span>
                          </Spinner>
                        </div>
                      ) : (
                        isPropertiesExpanded && (
                          <div style={{ marginTop: '10px' }}>
                            {/* Section 1: Properties Inspected in Period (Numerator) */}
                            <div style={{
                              backgroundColor: '#e8f5e9',
                              borderRadius: '8px',
                              padding: '12px',
                              marginBottom: '15px'
                            }}>
                              <h6 style={{
                                fontSize: '14px',
                                fontWeight: 'bold',
                                color: '#2e7d32',
                                marginBottom: '10px'
                              }}>
                                Properties Inspected in Period ({propertiesInspectedCount})
                              </h6>
                              <DataTable
                                columns={[
                                  {
                                    name: <div style={{ whiteSpace: 'normal', lineHeight: '1.3' }}>Property</div>,
                                    selector: row => row.propertyName,
                                    sortable: true,
                                    width: '180px',
                                    wrap: true,
                                    cell: row => (
                                      <a
                                        href={`/inspections/${row.propertyUuid}?startDate=${sharedStartDate}&endDate=${sharedEndDate}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        style={{ color: '#007bff', textDecoration: 'underline' }}
                                      >
                                        {row.propertyName}
                                      </a>
                                    )
                                  },
                                  { name: <div style={{ whiteSpace: 'normal', lineHeight: '1.3' }}>Last Inspection</div>, selector: row => row.lastInspection, sortable: true, width: '130px', wrap: true },
                                  { name: <div style={{ whiteSpace: 'normal', lineHeight: '1.3' }}>Health Score</div>, selector: row => row.healthScore, sortable: true, width: '110px', wrap: true },
                                  { name: <div style={{ whiteSpace: 'normal', lineHeight: '1.3' }}>Critical Points</div>, selector: row => row.criticalPoint, sortable: true, width: '110px', wrap: true },
                                  { name: <div style={{ whiteSpace: 'normal', lineHeight: '1.3' }}>Critical Evaluations</div>, selector: row => row.criticalEvaluations, sortable: true, width: '120px', wrap: true },
                                  { name: <div style={{ whiteSpace: 'normal', lineHeight: '1.3' }}># of Inspected Points</div>, selector: row => row.inspected, sortable: true, width: '120px', wrap: true },
                                  { name: <div style={{ whiteSpace: 'normal', lineHeight: '1.3' }}># of Total Points</div>, selector: row => row.totalPoints, sortable: true, width: '110px', wrap: true },
                                  { name: <div style={{ whiteSpace: 'normal', lineHeight: '1.3' }}>% Inspected</div>, selector: row => row.percentInspected, sortable: true, width: '110px', wrap: true },
                                  { name: <div style={{ whiteSpace: 'normal', lineHeight: '1.3' }}>Technician</div>, selector: row => row.lastUploader, sortable: true, width: '130px', wrap: true }
                                ]}
                                data={inspectedPropertiesDetails}
                                pagination
                                paginationPerPage={25}
                                paginationRowsPerPageOptions={[25, 50, 100]}
                                style={{ width: '100%' }}
                              />
                              <button
                                onClick={downloadDataTableCsv}
                                style={{
                                  marginTop: '10px',
                                  fontSize: '14px',
                                  fontWeight: 'bold',
                                  padding: '8px 16px',
                                  borderRadius: '8px',
                                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                                  transition: 'transform 0.3s',
                                  backgroundColor: '#28a745',
                                  color: '#ffffff',
                                  border: 'none',
                                  cursor: 'pointer'
                                }}
                              >
                                Download Inspected Properties (CSV)
                              </button>
                            </div>

                            {/* Section 2: All Company Properties (Denominator) */}
                            <div style={{
                              backgroundColor: '#f5f5f5',
                              borderRadius: '8px',
                              padding: '12px'
                            }}>
                              <h6 style={{
                                fontSize: '14px',
                                fontWeight: 'bold',
                                color: '#616161',
                                marginBottom: '10px'
                              }}>
                                All Company Properties ({totalPropertiesCount})
                              </h6>
                              <DataTable
                                columns={[
                                  {
                                    name: 'Property',
                                    selector: row => row.name,
                                    sortable: true,
                                    width: '250px',
                                    cell: row => (
                                      <a
                                        href={`/inspections/${row.uuid}?startDate=${sharedStartDate}&endDate=${sharedEndDate}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        style={{ color: '#007bff', textDecoration: 'underline' }}
                                      >
                                        {row.name}
                                      </a>
                                    )
                                  },
                                  {
                                    name: 'Last Report Run',
                                    selector: row => row.lastReportRun,
                                    format: row => row.lastReportRun ? moment(row.lastReportRun).format('MM/DD/YY') : '-',
                                    sortable: true,
                                    width: '150px',
                                    sortFunction: (rowA, rowB) => {
                                      const a = rowA.lastReportRun ? new Date(rowA.lastReportRun) : null;
                                      const b = rowB.lastReportRun ? new Date(rowB.lastReportRun) : null;
                                      if (!a && !b) return 0;
                                      if (!a) return 1;
                                      if (!b) return -1;
                                      return a.getTime() - b.getTime();
                                    }
                                  },
                                  {
                                    name: 'Last Evaluation',
                                    selector: row => row.last_evaluation_at,
                                    format: row => row.last_evaluation_at ? moment(row.last_evaluation_at).format('MM/DD/YY') : '-',
                                    sortable: true,
                                    width: '150px',
                                    sortFunction: (rowA, rowB) => {
                                      const a = rowA.last_evaluation_at ? new Date(rowA.last_evaluation_at) : null;
                                      const b = rowB.last_evaluation_at ? new Date(rowB.last_evaluation_at) : null;
                                      if (!a && !b) return 0;
                                      if (!a) return 1;
                                      if (!b) return -1;
                                      return a.getTime() - b.getTime();
                                    }
                                  },
                                  {
                                    name: 'Last Upload',
                                    selector: row => row.last_upload_at,
                                    format: row => row.last_upload_at ? moment(row.last_upload_at).format('MM/DD/YY') : '-',
                                    sortable: true,
                                    width: '150px',
                                    sortFunction: (rowA, rowB) => {
                                      const a = rowA.last_upload_at ? new Date(rowA.last_upload_at) : null;
                                      const b = rowB.last_upload_at ? new Date(rowB.last_upload_at) : null;
                                      if (!a && !b) return 0;
                                      if (!a) return 1;
                                      if (!b) return -1;
                                      return a.getTime() - b.getTime();
                                    }
                                  },
                                  {
                                    name: 'Inspected in Period',
                                    selector: row => row.inspectedInPeriod,
                                    cell: row => (
                                      <span style={{
                                        color: row.inspectedInPeriod ? '#2e7d32' : '#9e9e9e',
                                        fontWeight: row.inspectedInPeriod ? 'bold' : 'normal'
                                      }}>
                                        {row.inspectedInPeriod ? '● Yes' : 'No'}
                                      </span>
                                    ),
                                    sortable: true,
                                    width: '150px'
                                  }
                                ]}
                                data={allPropertiesData.map(p => ({
                                  ...p,
                                  inspectedInPeriod: inspectedPropertiesDetails.some(ip => ip.propertyName === p.name)
                                }))}
                                pagination
                                paginationPerPage={25}
                                paginationRowsPerPageOptions={[25, 50, 100]}
                                style={{ width: '100%' }}
                              />
                              <button
                                onClick={downloadAllPropertiesCsv}
                                style={{
                                  marginTop: '10px',
                                  fontSize: '14px',
                                  fontWeight: 'bold',
                                  padding: '8px 16px',
                                  borderRadius: '8px',
                                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                                  transition: 'transform 0.3s',
                                  backgroundColor: '#28a745',
                                  color: '#ffffff',
                                  border: 'none',
                                  cursor: 'pointer'
                                }}
                              >
                                Download All Properties (CSV)
                              </button>
                            </div>
                          </div>
                        )
                      )}
                    </>
                  )}
                </Card.Body>
              </Card>
            </Col>
            <Col md={isPropertiesWithChecksExpanded ? 12 : 6} lg={isPropertiesWithChecksExpanded ? 12 : 4}>
              <Card className="mb-3" style={{
                boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                borderRadius: '12px',
                borderColor: '#e0e0e0',
                transition: 'transform 0.3s',
                background: '#ffffff',
                width: '100%',
                overflowX: 'auto'
              }}>
                <Card.Body>
                  <Card.Title style={{ fontSize: '18px', fontWeight: 'bold' }}>
                    Properties Inspected w/Tasking Sheets
                  </Card.Title>
                  {!isPropertiesFetched ? (
                    <div style={{ marginTop: '20px', textAlign: 'center' }}>
                      <button
                        onClick={() => {
                          fetchFilteredPropertyActivityHistory();
                          fetchTotalPropertiesCount();
                        }}
                        disabled={isPropertiesCardLoading}
                        style={{
                          fontSize: '14px',
                          fontWeight: 'bold',
                          padding: '8px 16px',
                          borderRadius: '8px',
                          backgroundColor: '#007bff',
                          color: '#ffffff',
                          border: 'none',
                          cursor: isPropertiesCardLoading ? 'not-allowed' : 'pointer',
                          opacity: isPropertiesCardLoading ? 0.6 : 1
                        }}
                      >
                        {isPropertiesCardLoading ? 'Loading...' : 'Get Results'}
                      </button>
                    </div>
                  ) : (
                    <>
                      <Card.Text style={{ fontSize: '20px' }}>
                        {propertiesWithChecksCount} / {totalPropertiesCount}
                      </Card.Text>
                      <span
                        onClick={togglePropertiesWithChecksDetails}
                        style={{ cursor: 'pointer', color: '#007bff', fontSize: '14px' }}
                      >
                        {isPropertiesWithChecksExpanded ? 'Hide Details' : 'Show Details'}
                      </span>
                      {isPropertiesWithChecksLoading ? (
                        <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: '200px', marginTop: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                          <Spinner animation="border" role="status" style={{ width: '3rem', height: '3rem' }}>
                            <span className="sr-only">Loading...</span>
                          </Spinner>
                          <p style={{ marginTop: '15px', color: '#666', fontSize: '14px' }}>Loading tasking sheet details...</p>
                        </div>
                      ) : (
                        isPropertiesWithChecksExpanded && (
                          <div style={{ marginTop: '20px', width: '100%', overflowX: 'auto' }}>
                            <DataTable
                              title="Properties with Tasking Sheet Activity"
                              columns={[
                                {
                                  name: 'Property',
                                  selector: row => row.propertyName,
                                  sortable: true,
                                  minWidth: '250px',
                                  wrap: true,
                                  cell: row => (
                                    <a
                                      href={`/inspections/${row.propertyUuid}?startDate=${sharedStartDate}&endDate=${sharedEndDate}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      style={{ color: '#007bff', textDecoration: 'underline' }}
                                    >
                                      {row.propertyName}
                                    </a>
                                  )
                                },
                                { name: 'Last Check Date', selector: row => row.lastCheckDate, sortable: true, minWidth: '150px' },
                                { name: 'Last Check User', selector: row => row.lastCheckUser, sortable: true, minWidth: '200px', wrap: true }
                              ]}
                              data={propertiesWithChecksDetails}
                              dense
                              striped
                              highlightOnHover
                              pagination
                              paginationPerPage={25}
                              paginationRowsPerPageOptions={[25, 50, 100]}
                              style={{ fontSize: '12px' }}
                            />
                            <button
                              onClick={downloadPropertiesWithChecksCsv}
                              disabled={!propertiesWithChecksDetails.length}
                              style={{
                                marginTop: '10px',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                padding: '8px 16px',
                                borderRadius: '8px',
                                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                                backgroundColor: propertiesWithChecksDetails.length ? '#28a745' : '#6c757d',
                                color: '#ffffff',
                                border: 'none',
                                cursor: propertiesWithChecksDetails.length ? 'pointer' : 'not-allowed',
                                opacity: propertiesWithChecksDetails.length ? 1 : 0.6
                              }}
                            >
                              Download Table Data (CSV)
                            </button>
                          </div>
                        )
                      )}
                    </>
                  )}
                </Card.Body>
              </Card>
            </Col>
            <Col md={(isCriticalPointsExpanded || isCriticalEvalsExpanded) ? 12 : 6} lg={(isCriticalPointsExpanded || isCriticalEvalsExpanded) ? 12 : 4}>
              <Card className="mb-3" style={{
                boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                borderRadius: '12px',
                borderColor: '#e0e0e0',
                transition: 'transform 0.3s',
                background: '#ffffff',
                width: '100%',
                overflowX: 'auto'
              }}>
                <Card.Body>
                  <Card.Title style={{ fontSize: '22px', fontWeight: 'bold' }}>Critical Inspections</Card.Title>

                  {!isPropertiesFetched ? (
                    // Show "Get Results" button when not fetched (shares data with Properties card)
                    <div style={{ marginTop: '20px', textAlign: 'center' }}>
                      <button
                        onClick={() => {
                          fetchFilteredPropertyActivityHistory();
                          fetchTotalPropertiesCount();
                        }}
                        disabled={isPropertiesCardLoading}
                        style={{
                          fontSize: '14px',
                          fontWeight: 'bold',
                          padding: '8px 16px',
                          borderRadius: '8px',
                          backgroundColor: '#007bff',
                          color: '#ffffff',
                          border: 'none',
                          cursor: isPropertiesCardLoading ? 'not-allowed' : 'pointer',
                          opacity: isPropertiesCardLoading ? 0.6 : 1
                        }}
                      >
                        {isPropertiesCardLoading ? 'Loading...' : 'Get Results'}
                      </button>
                    </div>
                  ) : (
                    <>
                      {/* Critical Points Section */}
                      <div style={{ marginBottom: '15px' }}>
                        <div style={{ fontSize: '14px', color: '#666', fontWeight: '600', marginBottom: '5px' }}>
                          Critical Points
                          <span
                            onClick={toggleCriticalPointsDetails}
                            style={{ cursor: 'pointer', marginLeft: '10px', color: '#007bff', fontSize: '12px' }}
                          >
                            {isCriticalPointsExpanded ? 'Hide Details' : 'Show Details'}
                          </span>
                        </div>
                        <Card.Text style={{ fontSize: '20px', margin: '0' }}>
                          {criticalRedPoints}
                        </Card.Text>
                      </div>

                  {/* Critical Points Show Details */}
                  {isCriticalPointsExpanded && (
                    isCriticalPointsLoading ? (
                      <div className="d-flex flex-column justify-content-center align-items-center" style={{
                        height: '200px',
                        marginTop: '20px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px'
                      }}>
                        <Spinner animation="border" role="status" style={{ width: '3rem', height: '3rem' }}>
                          <span className="sr-only">Loading critical points details...</span>
                        </Spinner>
                        <p style={{ marginTop: '15px', color: '#666', fontSize: '14px' }}>
                          Loading critical points details...
                        </p>
                      </div>
                    ) : (
                      <div style={{ marginTop: '20px', marginBottom: '20px', width: '100%', overflowX: 'auto' }}>
                        <DataTable
                          title="Critical Points Details"
                          columns={[
                            {
                              name: <div style={{ whiteSpace: 'normal', lineHeight: '1.3' }}>Property</div>,
                              selector: row => row.property,
                              sortable: true,
                              width: '150px',
                              wrap: true,
                              cell: row => (
                                <a
                                  href={`/inspections/${row.propertyUuid}?startDate=${sharedStartDate}&endDate=${sharedEndDate}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  style={{ color: '#007bff', textDecoration: 'underline' }}
                                >
                                  {row.property}
                                </a>
                              )
                            },
                            {
                              name: <div style={{ whiteSpace: 'normal', lineHeight: '1.3' }}>Inspection Date</div>,
                              selector: row => row.inspectionDate,
                              cell: row => <span>{moment.tz(row.inspectionDate, 'YYYY-MM-DD HH:mm:ss', Utils.UTC_TZ).local().format('M-D-YY HH:mm')}</span>,
                              sortable: true,
                              width: '120px',
                              center: true
                            },
                            {
                              name: <div style={{ whiteSpace: 'normal', lineHeight: '1.3' }}>Equipment</div>,
                              selector: row => row.rootComp,
                              sortable: true,
                              width: '200px',
                              wrap: true,
                              style: {
                                backgroundColor: '#f0f0f0',
                                color: '#000000',
                              }
                            },
                            {
                              name: <div style={{ whiteSpace: 'normal', lineHeight: '1.3' }}>Point Name</div>,
                              selector: row => row.pointName,
                              sortable: true,
                              width: '150px',
                              wrap: true
                            },
                            {
                              name: <div style={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: '1.3' }}>Sound Signature™</div>,
                              selector: row => row.soundSignatures,
                              sortable: true,
                              width: '130px',
                              wrap: true,
                              center: true
                            },
                            {
                              name: <div style={{ whiteSpace: 'normal', lineHeight: '1.3' }}>Actions</div>,
                              selector: row => row.actions,
                              sortable: true,
                              width: '130px',
                              wrap: true,
                              center: true
                            },
                            {
                              name: <div style={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: '1.3' }}>Sound Energy™</div>,
                              selector: row => row.soundEnergy,
                              sortable: true,
                              sortFunction: (rowA, rowB) => {
                                const a = rowA.soundEnergy;
                                const b = rowB.soundEnergy;
                                if (a === "VISUAL" && b === "VISUAL") return 0;
                                if (a === "VISUAL") return 1;
                                if (b === "VISUAL") return -1;
                                return a - b;
                              },
                              width: '110px',
                              center: true
                            },
                            {
                              name: <div style={{ whiteSpace: 'normal', lineHeight: '1.3' }}>Evaluation</div>,
                              selector: row => row.evaluation,
                              cell: row => <EvaluationMark criticality={row.evaluation} />,
                              sortable: true,
                              width: '115px',
                              center: true
                            },
                            {
                              name: <div style={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: '1.3' }}>Sound Evaluation™</div>,
                              selector: row => row.soundEvaluator,
                              cell: row => <EvaluationMark criticality={row.soundEvaluator} />,
                              sortable: true,
                              width: '120px',
                              center: true
                            },
                            {
                              name: <div style={{ whiteSpace: 'normal', lineHeight: '1.3' }}>Technician</div>,
                              selector: row => row.technician,
                              sortable: true,
                              width: '130px',
                              wrap: true
                            }
                          ]}
                          data={criticalPointsDetails}
                          pagination
                          paginationPerPage={25}
                          paginationRowsPerPageOptions={[25, 50, 100]}
                          dense
                          highlightOnHover
                          noDataComponent="No critical points found."
                        />
                        <button
                          onClick={downloadCriticalPointsCsv}
                          disabled={!criticalPointsDetails.length}
                          style={{
                            marginTop: '10px',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            padding: '8px 16px',
                            borderRadius: '8px',
                            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                            transition: 'transform 0.3s',
                            backgroundColor: criticalPointsDetails.length ? '#28a745' : '#6c757d',
                            color: '#ffffff',
                            border: 'none',
                            cursor: criticalPointsDetails.length ? 'pointer' : 'not-allowed',
                            opacity: criticalPointsDetails.length ? 1 : 0.6
                          }}
                        >
                          Download Table Data (CSV)
                        </button>
                      </div>
                    )
                  )}

                  {/* Critical Evaluations Section */}
                  <div>
                    <div style={{ fontSize: '14px', color: '#666', fontWeight: '600', marginBottom: '5px' }}>
                      Critical Evaluations
                      <span
                        onClick={toggleCriticalEvalsDetails}
                        style={{ cursor: 'pointer', marginLeft: '10px', color: '#007bff', fontSize: '12px' }}
                      >
                        {isCriticalEvalsExpanded ? 'Hide Details' : 'Show Details'}
                      </span>
                    </div>
                    <Card.Text style={{ fontSize: '20px', margin: '0' }}>
                      {criticalEvaluationsCount}
                    </Card.Text>
                  </div>

                  {/* Critical Evaluations Show Details */}
                  {isCriticalEvalsExpanded && (
                    isCriticalEvalsLoading ? (
                      <div className="d-flex flex-column justify-content-center align-items-center" style={{
                        height: '200px',
                        marginTop: '20px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px'
                      }}>
                        <Spinner animation="border" role="status" style={{ width: '3rem', height: '3rem' }}>
                          <span className="sr-only">Loading critical evaluations details...</span>
                        </Spinner>
                        <p style={{ marginTop: '15px', color: '#666', fontSize: '14px' }}>
                          Loading critical evaluations details...
                        </p>
                      </div>
                    ) : (
                      <div style={{ marginTop: '20px', width: '100%', overflowX: 'auto' }}>
                        <DataTable
                          title="Critical Evaluations Details"
                          columns={[
                            {
                              name: 'Property',
                              selector: row => row.property,
                              sortable: true,
                              width: '150px',
                              wrap: true,
                              cell: row => (
                                <a
                                  href={`/inspections/${row.propertyUuid}?startDate=${sharedStartDate}&endDate=${sharedEndDate}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  style={{ color: '#007bff', textDecoration: 'underline' }}
                                >
                                  {row.property}
                                </a>
                              )
                            },
                            {
                              name: 'Equipment',
                              selector: row => row.equipment,
                              sortable: true,
                              width: '150px',
                              wrap: true
                            },
                            {
                              name: 'Created At',
                              selector: row => row.createdAt,
                              cell: row => <span>{moment.tz(row.createdAt, 'YYYY-MM-DD HH:mm:ss', Utils.UTC_TZ).local().format('M-D-YY')}</span>,
                              sortable: true,
                              width: '100px',
                              center: true
                            },
                            {
                              name: 'SoundEvaluator™',
                              selector: row => row.soundEvaluator,
                              cell: row => <EvaluationMark criticality={row.soundEvaluator} />,
                              sortable: true,
                              width: '130px',
                              center: true
                            },
                            {
                              name: 'Evaluation',
                              selector: row => row.evaluation,
                              cell: row => <EvaluationMark criticality={row.evaluation} />,
                              sortable: true,
                              width: '90px',
                              center: true
                            },
                            {
                              name: 'MPE Tags',
                              selector: row => row.mpeTagNames || row.mpeTags || [],
                              cell: row => (
                                <div>
                                  {(row.mpeTagNames || row.mpeTags || []).map((tag, index) => {
                                    const displayTag = tag.includes('^') ? tag.split('^')[1] : tag;
                                    return (
                                      <span
                                        key={index}
                                        style={{
                                          display: 'inline-block',
                                          padding: '0.25em 0.4em',
                                          fontSize: '90%',
                                          fontWeight: '700',
                                          lineHeight: '1',
                                          textAlign: 'center',
                                          whiteSpace: 'pre-wrap',
                                          wordWrap: 'break-word',
                                          verticalAlign: 'baseline',
                                          borderRadius: '0.25rem',
                                          color: '#fff',
                                          backgroundColor: '#007bff',
                                          margin: '0.1em',
                                          maxWidth: '25ch'
                                        }}
                                      >
                                        {displayTag}
                                      </span>
                                    );
                                  })}
                                </div>
                              ),
                              sortable: true,
                              sortFunction: (rowA, rowB) => {
                                const a = (rowA.mpeTagNames || rowA.mpeTags || []).length;
                                const b = (rowB.mpeTagNames || rowB.mpeTags || []).length;
                                return a - b;
                              },
                              width: '200px'
                            },
                            {
                              name: 'MPE Actions',
                              selector: row => row.mpeActionNames || row.mpeActions || [],
                              cell: row => (
                                <div>
                                  {(row.mpeActionNames || row.mpeActions || []).map((action, index) => {
                                    const displayAction = action.includes('^') ? action.split('^')[1] : action;
                                    return (
                                      <span
                                        key={index}
                                        style={{
                                          display: 'inline-block',
                                          padding: '0.25em 0.4em',
                                          fontSize: '90%',
                                          fontWeight: '700',
                                          lineHeight: '1',
                                          textAlign: 'center',
                                          whiteSpace: 'pre-wrap',
                                          wordWrap: 'break-word',
                                          verticalAlign: 'baseline',
                                          borderRadius: '0.25rem',
                                          color: '#fff',
                                          backgroundColor: '#007bff',
                                          margin: '0.1em',
                                          maxWidth: '25ch'
                                        }}
                                      >
                                        {displayAction}
                                      </span>
                                    );
                                  })}
                                </div>
                              ),
                              sortable: true,
                              sortFunction: (rowA, rowB) => {
                                const a = (rowA.mpeActionNames || rowA.mpeActions || []).length;
                                const b = (rowB.mpeActionNames || rowB.mpeActions || []).length;
                                return a - b;
                              },
                              width: '200px'
                            }
                          ]}
                          data={criticalEvalsDetails}
                          pagination
                          paginationPerPage={25}
                          paginationRowsPerPageOptions={[25, 50, 100]}
                          dense
                          highlightOnHover
                          noDataComponent="No critical evaluations found."
                        />
                        <button
                          onClick={downloadCriticalEvalsCsv}
                          disabled={!criticalEvalsDetails.length}
                          style={{
                            marginTop: '10px',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            padding: '8px 16px',
                            borderRadius: '8px',
                            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                            transition: 'transform 0.3s',
                            backgroundColor: criticalEvalsDetails.length ? '#28a745' : '#6c757d',
                            color: '#ffffff',
                            border: 'none',
                            cursor: criticalEvalsDetails.length ? 'pointer' : 'not-allowed',
                            opacity: criticalEvalsDetails.length ? 1 : 0.6
                          }}
                        >
                          Download Table Data (CSV)
                        </button>
                      </div>
                    )
                  )}
                    </>
                  )}
                </Card.Body>
              </Card>
            </Col>
            <Col md={isReportsExpanded ? 12 : 6} lg={isReportsExpanded ? 12 : 4}>
              <Card className="mb-3" style={{ boxShadow: '0 4px 8px rgba(0,0,0,0.2)', borderRadius: '12px', borderColor: '#e0e0e0', transition: 'transform 0.3s', background: '#ffffff', width: '100%' }}>
                <Card.Body>
                  <Card.Title style={{ fontSize: '18px', fontWeight: 'bold' }}>
                    Reports
                  </Card.Title>
                  {!isReportsFetched ? (
                    // Show "Get Results" button when not fetched
                    <div style={{ marginTop: '20px', textAlign: 'center' }}>
                      <button
                        onClick={fetchReportCount}
                        disabled={isReportsLoading}
                        style={{
                          fontSize: '14px',
                          fontWeight: 'bold',
                          padding: '8px 16px',
                          borderRadius: '8px',
                          backgroundColor: '#007bff',
                          color: '#ffffff',
                          border: 'none',
                          cursor: isReportsLoading ? 'not-allowed' : 'pointer',
                          opacity: isReportsLoading ? 0.6 : 1
                        }}
                      >
                        {isReportsLoading ? 'Loading...' : 'Get Results'}
                      </button>
                    </div>
                  ) : (
                    <>
                      <Card.Text style={{ fontSize: '20px' }}>{reportCount}</Card.Text>
                      <span
                        onClick={toggleReportsDetails}
                        style={{ cursor: 'pointer', color: '#007bff', fontSize: '14px' }}
                      >
                        {isReportsExpanded ? 'Hide Details' : 'Show Details'}
                      </span>
                      {isReportsExpanded && (
                    isReportsDetailsLoading ? (
                      <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: '200px', marginTop: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                        <Spinner animation="border" role="status" style={{ width: '3rem', height: '3rem' }}>
                          <span className="sr-only">Loading report details...</span>
                        </Spinner>
                        <p style={{ marginTop: '15px', color: '#666', fontSize: '14px' }}>Loading report details...</p>
                      </div>
                    ) : (
                      <div style={{ marginTop: '20px', width: '100%', overflowX: 'auto' }}>
                        <DataTable
                          title="Report Details"
                          columns={[
                            {
                              name: 'Report Link',
                              selector: row => row.link,
                              sortable: true,
                              format: row => row.link ? (
                                <a
                                  href={row.link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ color: '#007bff', fontSize: '12px' }}
                                >
                                  View
                                </a>
                              ) : 'N/A',
                              minWidth: '80px'
                            },
                            {
                              name: 'Created At',
                              selector: row => row.createdAt,
                              format: row => row.createdAt ? moment(row.createdAt).format('MM/DD/YY HH:mm') : 'N/A',
                              sortable: true,
                              minWidth: '140px'
                            },
                            {
                              name: 'Report Type',
                              selector: row => row.reportType,
                              sortable: true,
                              minWidth: '120px',
                              wrap: true
                            },
                            {
                              name: 'User',
                              selector: row => row.userName,
                              sortable: true,
                              minWidth: '150px',
                              wrap: true
                            },
                            {
                              name: 'Property',
                              selector: row => row.propertyName,
                              sortable: true,
                              minWidth: '150px',
                              wrap: true,
                              cell: row => row.propertyUuid ? (
                                <a
                                  href={`/inspections/${row.propertyUuid}?startDate=${sharedStartDate}&endDate=${sharedEndDate}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  style={{ color: '#007bff', textDecoration: 'underline' }}
                                >
                                  {row.propertyName}
                                </a>
                              ) : row.propertyName
                            }
                          ]}
                          data={reportDetailedData}
                          dense
                          striped
                          highlightOnHover
                          pagination
                          paginationPerPage={25}
                          paginationRowsPerPageOptions={[25, 50, 100]}
                          style={{ fontSize: '12px' }}
                        />
                        <button
                          onClick={downloadReportsCsv}
                          disabled={!reportDetailedData.length}
                          style={{
                            marginTop: '10px',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            padding: '8px 16px',
                            borderRadius: '8px',
                            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                            transition: 'transform 0.3s',
                            backgroundColor: reportDetailedData.length ? '#28a745' : '#6c757d',
                            color: '#ffffff',
                            border: 'none',
                            cursor: reportDetailedData.length ? 'pointer' : 'not-allowed',
                            opacity: reportDetailedData.length ? 1 : 0.6
                          }}
                        >
                          Download Table Data (CSV)
                        </button>
                      </div>
                    )
                  )}
                    </>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
      </Container>
    </div>
  );
};

export default CompanyAdminDashboard;